<template>
  <div class="seat-booking">
    <!-- Bus Loading Animation -->
    <BusLoadingAnimation v-if="isInitialLoading" />

    <div class="seat-booking__wrapper" v-else>

    <!-- Float Button Đặt Nhóm (outside page structure) -->
    <GroupBookingBanner @open-modal="showGroupBookingModal = true" />
    <GroupBookingModal
      :visible="showGroupBookingModal"
      @close="showGroupBookingModal = false"
    />

    <!-- Hold Ticket Info Banner -->
    <div
      v-if="holdTicketInfo"
      class="hold-ticket-info"
      :class="{ collapsed: isHoldTicketCollapsed }"
    >
      <div class="hold-ticket-summary">
        <div>
          <span class="hold-ticket">Vé đang được giữ</span>
          <span class="route">{{ holdTicketInfo.destination }}</span>
          <span class="hold-until">Còn: {{ remainingTime }}</span>
        </div>
        <button
          class="toggle-btn"
          @click="isHoldTicketCollapsed = !isHoldTicketCollapsed"
        >
          <span v-if="isHoldTicketCollapsed">▼</span>
          <span v-else>▲</span>
        </button>
      </div>
      <transition name="fade">
        <div v-show="!isHoldTicketCollapsed" class="hold-ticket-details">
          <div class="selected-info">
            <div class="time">
              {{ formatDateTime(holdTicketInfo.departure_datetime) ?? 'N/A' }}
            </div>
            <div class="seats">
              Ghế: {{ holdTicketInfo.slot_numbers.join(', ') }}
            </div>
            <div class="ticket-type" v-if="holdTicketInfo.ticketType">
              Loại vé: {{ holdTicketInfo.ticketType.type }}
            </div>
          </div>
          <div class="action-container">
            <!-- Các nút Tiếp tục/Hủy giữ vé giữ nguyên -->
            <button
              @click="confirmHoldTicket"
              class="confirm-hold-btn"
              :disabled="isProcessing.confirmHoldTicket"
            >
              <span v-if="isProcessing.confirmHoldTicket">
                <i class="fas fa-spinner fa-spin"></i> Đang xử lý...
              </span>
              <span v-else>Tiếp tục vé giữ</span>
            </button>
            <button
              @click="cancelHoldTicket(true)"
              class="cancel-hold-btn"
              :disabled="isProcessing.cancelHoldTicket"
            >
              <span v-if="isProcessing.cancelHoldTicket">
                <i class="fas fa-spinner fa-spin"></i> Đang xử lý...
              </span>
              <span v-else>Hủy</span>
            </button>
          </div>
        </div>
      </transition>
    </div>

    <!-- Step Navigation -->
    <div class="steps-wrapper">
      <div class="step-navigation">
        <div
          v-for="(step, index) in steps"
          :key="step.id"
          :class="[
            'step',
            {
              active: currentStep === index,
              completed: completedSteps[index]
            }
          ]"
          @click="navigateToStep(index)"
        >
          <div class="step-circle">
            <div class="step-number">
              <span v-if="!completedSteps[index]">{{ index + 1 }}</span>
              <i v-else-if="completedSteps[index]" class="fas fa-check"></i>
            </div>
          </div>
          <div class="step-label">{{ step.label }}</div>

          <div
            v-if="index < steps.length - 1"
            class="step-connector"
            :style="{
              backgroundColor: completedSteps[index] ? '#16a34a' : '#e2e8f0'
            }"
          ></div>
        </div>
      </div>
    </div>

    <!-- Navigation Buttons -->
    <div class="step-navigation-buttons">
      <div class="btn-container left">
        <button
          class="nav-btn back"
          @click="previousStep"
          v-show="currentStep > 0"
        >
          <i class="fas fa-arrow-left"></i>
          Quay lại
        </button>
      </div>
      <div class="btn-container right">
        <button
          class="nav-btn next"
          @click="nextStep"
          v-show="currentStep < steps.length - 1"
          :disabled="!canProceed || isProcessing.nextStep"
        >
          <span v-if="isProcessing.nextStep">
            <i class="fas fa-spinner fa-spin"></i> Đang xử lý...
          </span>
          <span v-else>
            Tiếp theo
            <i class="fas fa-arrow-right"></i>
          </span>
        </button>
      </div>
    </div>

    <!-- Step Content -->
    <div class="step-content">
      <!-- Location Selection Step -->
      <div v-show="currentStep === 0" class="step-page step-card">
        <div class="location-select">
          <h3 class="section-title">Chọn địa điểm</h3>
          <!-- Add month filter section -->
          <div class="filter-header">
            <div class="month-filter">
              <button
                v-for="filter in monthFilters"
                :key="filter.value"
                :class="[
                  'month-filter-btn',
                  { active: selectedMonthFilter === filter.value }
                ]"
                @click="handleMonthFilter(filter.value)"
              >
                {{ filter.label }}
              </button>
            </div>
            <div class="search-box">
              <i class="fas fa-search"></i>
              <input
                type="text"
                v-model="routeSearchQuery"
                placeholder="Tìm kiếm điểm đến..."
              />
            </div>
          </div>

          <div class="location-buttons">
            <div class="loading-overlay" v-if="isProcessing.fetchingTrips || isSearching">
              <div class="loading-spinner"></div>
            </div>
            
            <!-- Show loading skeleton when searching -->
            <div v-if="isSearching && !isProcessing.fetchingTrips" class="skeleton-container">
              <div class="skeleton-card" v-for="i in 3" :key="i"></div>
            </div>
            
            <!-- Show no results message when search has no results -->
            <div v-else-if="routeSearchQuery && filteredRoutes.length === 0 && !isSearching" class="no-results">
              <div class="no-results-content">
                <i class="fas fa-search"></i>
                <h4>Không tìm thấy kết quả</h4>
                <p>Không có địa điểm nào chứa từ khóa "{{ routeSearchQuery }}"</p>
                <button @click="routeSearchQuery = ''" class="clear-search-btn">
                  Xóa bộ lọc tìm kiếm
                </button>
              </div>
            </div>
            
            <!-- Show routes -->
            <button
              v-else
              v-for="route in filteredRoutes"
              :key="route.route_id"
              :class="[
                'location-btn',
                { active: selectedRoute === route.route_id }
              ]"
              @click="
                $event =>
                  handleRouteSelect(
                    route.route_id,
                    route.destination_location,
                    false,
                    $event
                  )
              "
            >
              <div class="route-info">
                <div class="cities">
                  <span class="to">{{ route.destination_location }}</span>
                </div>
                <div class="price">
                  chỉ từ {{ formatCurrency(route.price) }}
                </div>
              </div>
            </button>
          </div>

        </div>
      </div>

      <!-- Time Selection Step -->
      <div v-show="currentStep === 1" class="step-page step-card">
        <div class="time-select">
          <h3 class="section-title">Chọn thời gian</h3>

          <div class="sort-options">
            <span class="sort-label">Sắp xếp theo:</span>
            <div class="sort-buttons">
              <button
                @click="sortOption = 'asc'"
                :class="['sort-btn', { active: sortOption === 'asc' }]"
              >
                <i class="fas fa-sort-amount-down-alt"></i> Thời gian sớm nhất
              </button>
              <button
                @click="sortOption = 'desc'"
                :class="['sort-btn', { active: sortOption === 'desc' }]"
              >
                <i class="fas fa-sort-amount-down"></i> Thời gian muộn nhất
              </button>
            </div>
          </div>
          <div class="refresh-indicator" v-if="isRefreshingSeats">
            <small
              ><i class="fas fa-sync-alt fa-spin"></i> Đang cập nhật...</small
            >
          </div>
          <div v-if="isProcessing.fetchingTrips" class="skeleton-container">
            <div class="skeleton-card" v-for="i in 3" :key="i"></div>
          </div>
          <div v-else class="time-slots">
            <button
              v-for="trip in filteredTrips"
              :key="trip.trip_id"
              :class="[
                'time-btn',
                { active: selectedTrip?.trip_id === trip.trip_id }
              ]"
              @click="$event => handleTripSelect(trip, false, $event)"
            >
              <div class="name">
                {{ trip.name || 'Chuyến đi' }}
              </div>
              <div class="time">
                {{ formatDateTime(trip.departure_datetime) }}
              </div>
              <div class="seats-left">
                {{ getAvailableSeats(trip) }} ghế còn lại
              </div>
            </button>
          </div>
        </div>
      </div>

      <!-- Ticket Type Selection Step -->
      <div v-show="currentStep === 2" class="step-page step-card">
        <div class="ticket-type-select">
          <h3 class="section-title">Chọn loại vé</h3>
          <div class="ticket-types">
            <button
              v-for="ticket in visibleTicketTypes"
              :key="ticket.id"
              :class="[
                'ticket-type-btn',
                { active: selectedTicketType === ticket.ticket_type_id }
              ]"
              @click="handleTicketTypeSelect(ticket)"
            >
              <div class="ticket-type-info">
                <div class="ticket-name">{{ ticket.type }}</div>
                <div class="ticket-description">{{ ticket.description }}</div>
                <div class="ticket-price">
                  {{ formatCurrency(ticket.price) }}
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>

      <!-- Seat Selection Step -->
      <div v-show="currentStep === 3" class="step-page step-card">
        <div class="seat-selection" v-if="selectedTrip">
          <h3 class="section-title">
            {{
              selectedTrip?.vehicle?.type === 'over_night'
                ? 'Chọn giường nằm'
                : 'Chọn chỗ ngồi'
            }}
          </h3>

          <!-- Special ticket notification -->
          <div v-if="isSpecialTicketType" class="special-ticket-notification">
            <div class="notification-icon">
              <svg
                height="30px"
                width="30px"
                viewBox="0 0 512 512"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g>
                  <path
                    fill="#000000"
                    d="M510.256,187.787c-2.16-7.414-6.118-13.725-11.079-18.567c-7.477-7.3-16.818-11.393-26.211-13.777 c-9.43-2.386-19.109-3.114-28.034-3.124c-7.732,0-14.89,0.562-20.744,1.219h0.016c-4.732,0.531-9.07,1.426-12.788,2.103 c-3.697,0.708-6.821,1.156-8.597,1.136l-0.557-0.042c-0.031-0.167-0.063-0.261-0.094-0.48c-2.63-18.598-4.03-36.864-9.06-55.494 c-5.004-18.619-13.976-37.457-30.986-55.826c-8.07-8.83-17.973-13.454-27.476-13.403c-8.43-0.01-15.98,3.489-21.312,8.779 c-5.337,5.279-8.763,12.517-8.784,20.504c-0.005,3.686,0.88,7.466,2.484,11.028l-0.067,0.031 c4.992,13.434,7.794,25.784,7.768,35.719c0,5.935-0.948,10.955-2.754,15.141c-2.77,6.227-7.196,11.111-15.923,15.193 c-8.695,4.019-21.816,6.685-40.06,6.664c-24.388,0.042-39.4-4.759-47.454-11.039c-4.082-3.155-6.697-6.623-8.529-10.819 c-1.807-4.186-2.754-9.206-2.754-15.141c-0.026-9.935,2.77-22.285,7.768-35.719l-0.068-0.02c1.604-3.582,2.489-7.352,2.485-11.028 c-0.021-7.997-3.448-15.235-8.784-20.514c-5.332-5.29-12.882-8.789-21.311-8.789c-9.502-0.042-19.406,4.571-27.476,13.412 c-17.01,18.359-25.982,37.207-30.985,55.826c-5.035,18.63-6.431,36.896-9.06,55.494c-0.032,0.229-0.063,0.312-0.099,0.48 l-0.552,0.042c-1.77,0.02-4.894-0.428-8.596-1.136c-3.718-0.687-8.055-1.572-12.793-2.103h0.01 c-5.836-0.646-13.001-1.208-20.733-1.219c-11.892,0.031-25.143,1.25-37.338,6.134c-6.087,2.458-11.939,5.904-16.906,10.768 c-4.962,4.842-8.919,11.153-11.08,18.567C0.563,191.806,0,196.014,0,200.282c0.099,18.276,9.836,37.583,27.684,53.817 c16.844,15.245,41.243,27.617,72.728,33.24c0.114,0.489,0.223,0.896,0.344,1.458c1.874,8.664,4.102,25.242,8.247,44.591 c6.285,29.115,16.896,65.167,39.16,94.835c11.142,14.798,25.31,27.981,43.201,37.364c17.879,9.404,39.378,14.902,64.636,14.892 c37.978,0.083,67.422-12.58,88.905-31.667c32.281-28.669,47.173-70.136,55.613-104.562c4.186-17.234,6.706-32.854,8.654-44.225 c0.912-5.353,1.708-9.748,2.4-12.674c31.496-5.624,55.9-17.994,72.744-33.25c17.848-16.234,27.58-35.541,27.684-53.817 C512,196.014,511.438,191.806,510.256,187.787z M165.549,59.409c5.118-5.456,9.022-6.508,11.804-6.55c2.499,0,4.665,1,6.316,2.615 c1.651,1.624,2.473,3.696,2.453,5.352l-0.625,2.572l-0.261,0.522l-0.198,0.541c-5.508,14.923-9.076,29.376-9.101,43.133 c0,8.195,1.312,16.192,4.498,23.586c2.526,5.946,6.404,11.288,11.32,15.912c-7.967-0.167-16.605-0.292-24.993-0.396 c-13.745-0.168-26.596-0.25-34.136-0.303c1.818-14.35,3.453-27.606,6.847-40.05C143.884,90.254,150.908,75.321,165.549,59.409z M318.981,441.844c-16.234,10.59-36.442,17.276-62.98,17.307c-33.38-0.083-56.795-10.528-74.784-26.315 c-12.834-11.267-22.712-25.689-30.455-41.195c0.901-4.78,3.015-9.133,6.436-13.392c6.935-8.612,19.78-16.308,36.791-21.514 c16.974-5.258,37.921-8.122,60.576-8.122c30.204-0.031,57.394,5.144,76.138,14.1c9.383,4.456,16.584,9.809,21.234,15.536 c4.483,5.581,6.706,11.33,6.893,17.994c-0.625,1.156-1.15,2.364-1.801,3.499C347.508,416.477,335.189,431.243,318.981,441.844z M470.002,238.292c-14.417,13.058-36.708,24.524-67.495,29.033l-2.374,0.344l-1.994,1.333c-2.578,1.79-3.583,3.53-4.39,4.904 c-1.431,2.708-2.014,4.927-2.702,7.571c-1.203,4.864-2.213,10.954-3.473,18.327c-3.228,19.193-8.054,46.892-18.27,73.634 c-1.239-2.072-2.593-4.082-4.134-5.957c-10.201-12.444-25.977-21.139-44.992-27.054c-19.046-5.873-41.524-8.872-65.615-8.872 c-32.12,0.02-61.362,5.28-83.464,15.746c-11.039,5.269-20.364,11.881-27.138,20.18c-0.822,1-1.468,2.124-2.207,3.177 c-3.9-10.57-7.082-21.233-9.544-31.448c-1.562-6.426-2.837-12.549-3.988-18.442c6.936,1.916,14.099,2.937,21.363,2.937 c20.879-0.011,42.372-7.331,61.09-19.443c18.708-12.132,34.692-29.126,44.258-48.954c5.878-12.204,8.32-24.596,8.315-36.312 c-0.01-18.692-6.124-35.635-14.272-48.276c-1.229-1.906-2.582-3.509-3.9-5.207c3.54,0.198,7.086,0.406,10.924,0.406 c26.914-0.042,46.606-4.947,60.424-15.433c6.856-5.227,11.97-11.913,15.141-19.306c3.186-7.394,4.499-15.392,4.499-23.586 c-0.026-13.757-3.592-28.21-9.101-43.133l-0.204-0.541l-0.255-0.511l-0.625-2.593c-0.021-1.646,0.802-3.728,2.447-5.341 c1.656-1.615,3.822-2.615,6.321-2.615c2.775,0.042,6.685,1.094,11.804,6.55c14.641,15.912,21.665,30.845,26.075,46.934 c4.384,16.068,5.821,33.448,8.523,52.869c0.39,2.78,1.125,5.478,2.411,8.039c1.869,3.842,5.321,7.143,8.992,8.778 c3.676,1.687,7.205,2.052,10.366,2.073c4.463-0.021,8.514-0.771,12.48-1.49c3.952-0.739,7.779-1.488,11.258-1.874l0.016-0.01 c5.252-0.584,11.662-1.084,18.358-1.084c10.289-0.021,21.228,1.26,29.356,4.582c4.082,1.646,7.42,3.728,9.95,6.217 c2.53,2.509,4.363,5.373,5.556,9.362c0.562,1.906,0.88,4.072,0.88,6.477C490.772,210.54,484.482,225.254,470.002,238.292z"
                  ></path>
                  <path
                    fill="#000000"
                    d="M207.474,389.839c-6.873,0-12.444,8.758-12.444,19.546c0,10.799,5.572,19.557,12.444,19.557 c6.867,0,12.439-8.758,12.439-19.557C219.912,398.597,214.341,389.839,207.474,389.839z"
                  ></path>
                  <path
                    fill="#000000"
                    d="M287.449,389.839c-6.873,0-12.444,8.758-12.444,19.546c0,10.799,5.572,19.557,12.444,19.557 c6.867,0,12.439-8.758,12.439-19.557C299.888,398.597,294.316,389.839,287.449,389.839z"
                  ></path>
                  <path
                    fill="#000000"
                    d="M316.774,229.898c-9.325,0-16.886,7.56-16.886,16.891c0,9.32,7.56,16.88,16.886,16.88 c9.325,0,16.885-7.56,16.885-16.88C333.659,237.459,326.098,229.898,316.774,229.898z"
                  ></path>
                </g>
              </svg>
            </div>
            <div class="notification-content">
              <h4>
                {{ getSelectedTicketTypeName }} (không cần chọn vị trí
                ghế/giường)
              </h4>
              <p>
                Loại vé này không yêu cầu chọn chỗ ngồi cụ thể. Vui lòng nhấn
                "Tiếp theo" để điền thông tin cá nhân.
              </p>
            </div>
          </div>

          <div v-if="isSpecialTicketType" class="special-ticket-quantity">
            <div class="quantity-container">
              <h4>Số lượng vé</h4>
              <div class="quantity-controls">
                <button
                  type="button"
                  class="quantity-btn"
                  @click="decreaseTicketQuantity"
                  :disabled="specialTicketQuantity <= 1"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                    <g
                      id="SVGRepo_tracerCarrier"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    ></g>
                    <g id="SVGRepo_iconCarrier">
                      <path
                        d="M6 12L18 12"
                        stroke="#000000"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></path>
                    </g>
                  </svg>
                </button>
                <input
                  type="number"
                  v-model="specialTicketQuantity"
                  min="1"
                  max="10"
                  class="quantity-input"
                  @input="validateQuantity"
                />
                <button
                  type="button"
                  class="quantity-btn"
                  @click="increaseTicketQuantity"
                  :disabled="specialTicketQuantity >= 10"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                    <g
                      id="SVGRepo_tracerCarrier"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    ></g>
                    <g id="SVGRepo_iconCarrier">
                      <path
                        d="M4 12H20M12 4V20"
                        stroke="#000000"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></path>
                    </g>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- Hide seat layout for special tickets -->
          <SeatLayout
            v-if="
              selectedTrip &&
              selectedTrip.vehicle &&
              selectedTrip.vehicle.type &&
              !isSpecialTicketType
            "
            :key="selectedTrip.trip_id"
            :vehicle-type="selectedTrip.vehicle.type"
            :selected-seats="selectedSeats"
            :booked-seats="getBookedSeats"
            :held-seats="heldSeats"
            :locked-seats="selectedTrip.locked_seats"
            @seat-selected="handleSeatSelect"
          />
        </div>

        <!-- Booking Summary -->
        <div class="booking-summary" v-if="selectedTrip">
          <div class="selected-info">
            <div class="route" v-if="to">
              {{ to }}
            </div>
            <div class="time" v-if="selectedTrip">
              {{ formatDateTime(selectedTrip.departure_datetime) }}
            </div>
            <div class="ticket-type" v-if="selectedTicketType">
              Loại vé: {{ getSelectedTicketTypeName }}
            </div>
            <div class="seats" v-if="!isSpecialTicketType">
              Ghế đã chọn: {{ selectedSeats.join(', ') }}
            </div>
          </div>

          <div class="total-price">
            <span class="amount">{{
              formatCurrency(calculateTotalPrice)
            }}</span>
          </div>
        </div>
      </div>

      <div v-show="currentStep === 4" class="step-page step-card addon-step">
        <!-- Sticky header for mobile -->
        <div class="addon-sticky-header">
          <div class="addon-sticky-info">
            <span class="addon-sticky-route">{{ to }}</span>
            <span class="addon-sticky-price">{{ formatCurrency(calculateTotalPrice + calculateAddonTotal()) }}</span>
          </div>
          <button
            class="addon-sticky-btn"
            @click="nextStep"
            :disabled="!canProceed || isProcessing.nextStep"
          >
            Tiếp tục
          </button>
        </div>
        <AddonServices
          ref="addonServicesRef"
          :trip="selectedTrip"
          :destination="to"
          :ticketTypeName="getSelectedTicketTypeName"
          :selectedSeats="selectedSeats"
          :isSpecialTicket="isSpecialTicketType"
          :specialTicketQuantity="specialTicketQuantity"
          :baseTotalPrice="calculateTotalPrice"
          @update:selectedAddons="selectedAddons = $event"
        />
      </div>

      <!-- Customer Information Step -->
      <div v-show="currentStep === 5" class="step-page step-card">
        <div class="customer-info">
          <h3 class="section-title">
            Thông tin hành khách
          </h3>

          <!-- Person tabs navigation -->
          <div class="person-tabs">
            <div
              v-for="(person, index) in displayedGroupMembers"
              :key="index"
              :class="['person-tab', { active: activeMemberIndex === index }]"
              @click="switchToMember(index)"
            >
              <span class="tab-badge" :class="index === 0 ? 'leader-badge' : 'member-badge'">
                {{ index === 0 ? 'TRƯỞNG NHÓM' : 'THÀNH VIÊN NHÓM' }}
              </span>
              <span>{{
                person.name ||
                (isSpecialTicketType
                  ? `Hành khách ${index + 1}`
                  : `Hành khách ${index + 1}`)
              }}</span>
              <button
                v-if="!isSpecialTicketType && groupMembers.length > 1"
                class="remove-tab-btn"
                @click.stop="removeMember(index)"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>

          <form class="customer-form" @submit.prevent="confirmBooking">
            <!-- ====== LEADER / PASSENGER #1 FORM ====== -->
            <template v-if="activeMemberIndex === 0">
              <div class="form-group">
                <label for="name">Họ và tên</label>
                <input
                  id="name"
                  v-model="currentMember.name"
                  type="text"
                  required
                  placeholder="Nhập họ và tên"
                />
              </div>

              <div class="form-group">
                <label for="phone">Số điện thoại (Sử dụng Zalo nếu có)</label>
                <input
                  id="phone"
                  v-model="currentMember.phone"
                  type="tel"
                  required
                  placeholder="Nhập số điện thoại"
                  :class="{ 'input-error': phoneError }"
                />
                <div v-if="phoneError" class="error-message">
                  {{ phoneError }}
                </div>
              </div>

              <div class="form-group">
                <label for="email">Email</label>
                <input
                  id="email"
                  v-model="currentMember.email"
                  type="email"
                  required
                  placeholder="Nhập email"
                  :class="{ 'input-error': emailError }"
                />
                <div v-if="emailError" class="error-message">
                  {{ emailError }}
                </div>
              </div>

              <div class="form-group">
                <label for="cccd">Số Căn cước công dân (Bắt buộc)</label>
                <input
                  id="cccd"
                  v-model="currentMember.cccd"
                  type="text"
                  :class="{ 'input-error': cccdError }"
                  :aria-invalid="!!cccdError"
                  required
                  placeholder="Nhập số CCCD 12 số"
                  @input="onCccdInput(currentMember.cccd)"
                  inputmode="numeric"
                />
                <div v-if="cccdError" class="error-message">
                  {{ cccdError }}
                </div>
              </div>



              <!-- Split DOB: Day/Month manual + Year auto-filled from CCCD -->
              <div class="form-group">
                <label>Ngày/tháng/năm sinh (CCCD)</label>
                <div class="dob-split-row">
                  <div class="dob-daymonth">
                    <input
                      v-model="currentMember.birthDay"
                      type="number"
                      min="1"
                      max="31"
                      placeholder="Ngày"
                      class="dob-input"
                    />
                    <span class="dob-separator">-</span>
                    <input
                      v-model="currentMember.birthMonth"
                      type="number"
                      min="1"
                      max="12"
                      placeholder="Tháng"
                      class="dob-input"
                    />
                  </div>
                  <div class="dob-year">
                    <input
                      v-model="currentMember.birthYear"
                      type="number"
                      min="1900"
                      :max="new Date().getFullYear()"
                      placeholder="Năm sinh"
                      class="dob-input dob-year-input"
                    />
                  </div>
                </div>
              </div>

              <!-- Address on CCCD -->
              <div class="form-group">
                <label for="cccdAddress">Địa chỉ trên CCCD</label>
                <input
                  id="cccdAddress"
                  v-model="currentMember.cccdAddress"
                  type="text"
                  required
                  placeholder="Nhập địa chỉ ghi trên CCCD"
                />
              </div>
            </template>

            <!-- ====== MEMBER / PASSENGER #2+ FORM ====== -->
            <template v-else>
              <div class="form-group">
                <label for="name">Họ và tên</label>
                <input
                  id="name"
                  v-model="currentMember.name"
                  type="text"
                  required
                  placeholder="Nhập họ và tên"
                />
              </div>

              <div class="form-group">
                <label for="phone">Số điện thoại (Sử dụng Zalo nếu có)</label>
                <input
                  id="phone"
                  v-model="currentMember.phone"
                  type="tel"
                  placeholder="Nhập số điện thoại"
                  :class="{ 'input-error': phoneError }"
                />
                <div v-if="phoneError" class="error-message">
                  {{ phoneError }}
                </div>
              </div>

              <div class="form-group">
                <label for="email">Email</label>
                <input
                  id="email"
                  v-model="currentMember.email"
                  type="email"
                  placeholder="Nhập email"
                  :class="{ 'input-error': emailError }"
                />
                <div v-if="emailError" class="error-message">
                  {{ emailError }}
                </div>
              </div>

              <!-- Only expose companion identity when supplied; it remains optional. -->
              <div
                v-if="currentMember.cccd !== undefined && currentMember.cccd !== null && currentMember.cccd !== ''"
                class="form-group"
              >
                <label for="cccd">Số Căn cước công dân (Không bắt buộc)</label>
                <input
                  id="cccd"
                  v-model="currentMember.cccd"
                  type="text"
                  inputmode="numeric"
                  placeholder="Nhập số CCCD 12 số"
                  :class="{ 'input-error': cccdError }"
                  :aria-invalid="!!cccdError"
                  @input="onCccdInput(currentMember.cccd)"
                />
                <div v-if="cccdError" class="error-message">{{ cccdError }}</div>
              </div>

              <!-- Full DOB picker for members -->
              <div class="form-group">
                <label for="birthDate" class="label-highlight">Ngày/tháng/năm sinh chính xác (ghi trên CCCD)</label>
                <input
                  id="birthDate"
                  v-model="currentMember.birthDate"
                  type="date"
                  required
                  class="date-input"
                />
              </div>
            </template>

            <!-- ====== SHARED FIELDS ====== -->
            <div
              class="form-group form-group-select"
              v-if="!isSpecialTicketType"
            >
              <label for="pickupLocation">Điểm đón</label>
              <select
                id="pickupLocation"
                v-model="currentMember.pickupLocationId"
                required
                :disabled="pickupLocations.length === 0"
              >
                <option value="" disabled selected>
                  {{
                    pickupLocations && pickupLocations.length > 0
                      ? 'Chọn điểm đón'
                      : 'Đang tải điểm đón...'
                  }}
                </option>
                <option
                  v-for="location in pickupLocations"
                  :key="location.pickup_location_id"
                  :value="location.pickup_location_id"
                >
                  {{ location.location_name }} ({{ location.address }})
                </option>
              </select>
              <span class="select-focus-indicator"></span>
              <!-- Debug output -->
              <div v-if="DEBUG_MODE" class="debug-info">
                Số điểm đón:
                {{ pickupLocations ? pickupLocations.length : 0 }}
              </div>
            </div>

            <div class="form-group" v-if="isSpecialTicketType">
              <div class="special-ticket-info">
                <i class="fas fa-info-circle"></i>
                <span
                  >Vé {{ getSelectedTicketTypeName }} không cần chọn điểm
                  đón</span
                >
              </div>
            </div>

            <div class="form-group">
              <label for="memberNotes">Ghi chú / Yêu cầu đặc biệt (Không bắt buộc)</label>
              <input
                id="memberNotes"
                v-model="currentMember.notes"
                type="text"
                maxlength="500"
                placeholder="Ví dụ: Ăn chay, không ăn hành, dị ứng hải sản, say xe..."
              />
            </div>

            <div class="booking-summary" v-if="selectedTrip">
              <div class="selected-info">
                <div class="route" v-if="to">{{ to }}</div>
                <div class="time" v-if="selectedTrip">
                  {{ formatDateTime(selectedTrip.departure_datetime) }}
                </div>
                <div class="ticket-type" v-if="selectedTicketType">
                  Loại vé: {{ getSelectedTicketTypeName }}
                </div>
                <div class="seats" v-if="!isSpecialTicketType">
                  Ghế đã chọn: {{ selectedSeats.join(', ') }}
                </div>
                <div class="group-info">
                  Số hành khách:
                  {{
                    isSpecialTicketType
                      ? specialTicketQuantity
                      : selectedSeats.length
                  }}
                </div>

                <!-- Phần addon services được giữ nguyên -->
                <div class="addons-info" v-if="selectedAddons.length > 0">
                  <h4>Dịch vụ bổ sung</h4>
                  <ul class="selected-addons-list">
                    <li
                      class="selected-addon-item"
                      v-for="addon in selectedAddons"
                      :key="addon.productId"
                    >
                      <span class="selected-addon-name">{{
                        addon.name || getAddonName(addon.productId)
                      }}</span>
                      <span class="selected-addon-quantity"
                        >x{{ addon.quantity }}</span
                      >
                      <span class="selected-addon-price">{{
                        formatCurrency(
                          addon.price ||
                            getAddonPrice(addon.productId) * addon.quantity
                        )
                      }}</span>
                    </li>
                  </ul>
                  <div class="addons-total">
                    <span>Tổng phụ thu</span>
                    <span>{{ formatCurrency(calculateAddonTotal()) }}</span>
                  </div>
                </div>
              </div>
              <div class="total-price">
                <span class="amount">{{
                  formatCurrency(calculateTotalWithAddons())
                }}</span>
              </div>
            </div>

            <button
              type="submit"
              class="confirm-booking-btn"
              :disabled="isProcessing.confirmBooking"
              @click.prevent="confirmBooking"
            >
              <span v-if="isProcessing.confirmBooking">
                <i class="fas fa-spinner fa-spin"></i> Đang xử lý...
              </span>
              <span v-else>Xác nhận đặt vé</span>
            </button>
          </form>

          <div
            class="form-group"
            v-if="isSpecialTicketType && specialTicketQuantity > 1"
          >
            <div class="special-ticket-info">
              <i class="fas fa-info-circle"></i>
              <span
                >Vé {{ getSelectedTicketTypeName }}: Vui lòng điền thông tin cho
                tất cả {{ specialTicketQuantity }} hành khách.</span
              >
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="step-navigation-buttons">
      <div class="btn-container left">
        <button
          class="nav-btn back"
          @click="previousStep"
          v-show="currentStep > 0"
        >
          <i class="fas fa-arrow-left"></i>
          Quay lại
        </button>
      </div>
      <div class="btn-container right">
        <button
          class="nav-btn next"
          @click="nextStep"
          v-show="currentStep < steps.length - 1"
          :disabled="!canProceed || isProcessing.nextStep"
        >
          <span v-if="isProcessing.nextStep">
            <i class="fas fa-spinner fa-spin"></i> Đang xử lý...
          </span>
          <span v-else>
            Tiếp theo
            <i class="fas fa-arrow-right"></i>
          </span>
        </button>
      </div>
    </div>

    </div>
  </div>
</template>

<script>
import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  watch,
  nextTick,
  defineAsyncComponent
} from 'vue'
import { useRoute } from 'vue-router'

import BusLoadingAnimation from '@/components/common/BusLoadingAnimation.vue'
import router from '@/router'
// import CryptoJS from 'crypto-js'
import { secureLocalStorage, createBookingToken } from '@/utils/encryption'
import { SelectionError } from '@/services/checkoutV2Selection'
import { sharedVueCheckout } from '@/services/checkoutV2Flow'
import { CHECKOUT_V2_ENABLED } from '@/services/checkoutV2Flag'
import { rememberCheckout } from '@/services/checkoutV2Bridge'

// The shared instance owns the draft; AddonServices writes its cart into the same one.
const vueFlow = CHECKOUT_V2_ENABLED ? sharedVueCheckout() : null
import axiosInstance from '@/plugins/axios'
import { ToastService } from '@/services/ToastService'
import AddonServices from '@/components/Booking/AddonServices.vue'
import GroupBookingBanner from '@/components/Booking/GroupBookingBanner.vue'
import GroupBookingModal from '@/components/Booking/GroupBookingModal.vue'
import debounce from 'lodash/debounce'
import throttle from 'lodash/throttle'

const SeatLayout = defineAsyncComponent(() =>
  import('@/components/Booking/SeatLayout.vue')
)

const isProd = process.env.VUE_APP_IS_PROD === 'true'
const API_BASE_URL = isProd
  ? process.env.VUE_APP_BACKEND_URL_PROD
  : process.env.VUE_APP_BACKEND_URL_DEV

export default {
  name: 'SeatBooking',
  components: {
    SeatLayout,
    AddonServices,
    BusLoadingAnimation,
    GroupBookingBanner,
    GroupBookingModal
  },
  setup () {
    const isHoldTicketCollapsed = ref(true)
    const showGroupBookingModal = ref(false)
    const isInitialLoading = ref(true)
    const isProcessing = ref({
      nextStep: false,
      confirmBooking: false,
      confirmHoldTicket: false,
      cancelHoldTicket: false,
      holdSelectedSeats: false,
      fetchingTrips: false,
      fetchingRoutes: false,
      fetchingTicketTypes: false,
      fetchingPickupLocations: false,
      loadingMoreTrips: false
    })
    const DEBUG_MODE = ref(false)
    const routes = ref([])
    const trips = ref([])
    const bookings = ref([])
    const addonServicesRef = ref(null) // Ref for AddonServices component
    const selectedRoute = ref(null)
    const selectedTrip = ref(null)
    const selectedTicketType = ref(null)
    const to = ref('')
    const selectedSeats = ref([])
    const heldSeats = ref([])
    const pickupLocations = ref([])
    const specialTicketQuantity = ref(1)
    const sortOption = ref('asc')
    const groupMembers = ref([
      {
        name: '',
        phone: '',
        email: '',
        birthYear: null,
        pickupLocationId: '',
        gender: '',
        cccd: '',
        cccdAddress: '',
        birthDay: '',
        birthMonth: '',
        notes: ''
      }
    ])
    const seatRefreshInterval = ref(null)
    const isRefreshingSeats = ref(false)

    const activeMemberIndex = ref(0)
    const selectedAddons = ref([])

    const addonProducts = ref([])

    const displayedGroupMembers = computed(() => {
      // Show all group members for both special and regular tickets
      return groupMembers.value
    })

    const currentMember = computed({
      get: () => groupMembers.value[activeMemberIndex.value],
      set: val => {
        groupMembers.value[activeMemberIndex.value] = val
      }
    })

    const addNewMember = () => {
      if (groupMembers.value.length < selectedSeats.value.length) {
        groupMembers.value.push({
          name: '',
          phone: '',
          email: '',
          birthYear: null,
          pickupLocationId: '',
          gender: '',
          cccd: '',
          cccdAddress: '',
          birthDate: '',
          birthDay: '',
          birthMonth: '',
          notes: ''
        })
        activeMemberIndex.value = groupMembers.value.length - 1
      }
    }

    const removeMember = index => {
      if (groupMembers.value.length > selectedSeats.value.length) {
        groupMembers.value.splice(index, 1)
        if (index <= activeMemberIndex.value) {
          activeMemberIndex.value = Math.max(0, activeMemberIndex.value - 1)
        }
      }
    }

    const switchToMember = index => {
      activeMemberIndex.value = index
    }

    const ticketTypes = ref([])

    const currentStep = ref(0)
    const selectedMonthFilter = ref('current')
    const currentMonth = new Date().getMonth()
    const currentYear = new Date().getFullYear()
    const phoneError = ref('')
    const emailError = ref('')
    const getCCCDValidationMessage = (value, required = false) => {
      if (value === undefined || value === null || value === '') {
        return required ? 'Vui lòng nhập số CCCD' : ''
      }
      // The length check also rejects a trailing newline (JavaScript $ permits one).
      return typeof value === 'string' && value.length === 12 && /^[0-9]{12}$/.test(value)
        ? ''
        : 'Số CCCD phải bao gồm đúng 12 chữ số'
    }
    const cccdError = computed(() => getCCCDValidationMessage(
      currentMember.value?.cccd,
      activeMemberIndex.value === 0
    ))

    const routeSearchQuery = ref('')
    const isSearching = ref(false)

    // Digits of a pasted or typed identity number. NFKC folds full-width and Arabic-Indic
    // numerals to ASCII first, because those arrive with a paste from Zalo or a PDF and the
    // ASCII-only check below would otherwise erase the whole value. The length is left alone so
    // the twelve-digit rule rejects an overlong number out loud instead of shortening it.
    const onCccdInput = cccdValue => {
      parseCCCD(String(cccdValue ?? '').normalize('NFKC').replace(/\D/g, ''))
    }

    // CCCD Smart Auto-fill: parse citizen ID to extract gender and birth year
    const parseCCCD = (cccdValue) => {
      const member = groupMembers.value[activeMemberIndex.value]
      if (!member) return

      // Keep the source intact so malformed or overlong input can be corrected.
      member.cccd = cccdValue
      if (getCCCDValidationMessage(cccdValue, true)) return

      const fourthChar = cccdValue.charAt(3)
      const yearDigits = cccdValue.substring(4, 6)

      // Determine century from 4th character (0/1 → 1900s, 2/3 → 2000s)
      let century = null
      if (fourthChar === '0' || fourthChar === '1') {
        century = 19
      } else if (fourthChar === '2' || fourthChar === '3') {
        century = 20
      }

      // Parse birth year from 5th-6th characters
      if (century !== null && yearDigits) {
        const yearNum = parseInt(yearDigits, 10)
        if (!isNaN(yearNum)) {
          member.birthYear = century * 100 + yearNum
        }
      }
    }

    const formatCurrency = amount => {
      const num = Number(amount)

      return num.toLocaleString('vi-VN') + 'đ'
    }

    // Function to remove Vietnamese accents
    const removeVietnameseAccents = (str) => {
      return str
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
    }

    const filteredRoutes = computed(() => {
      if (!routeSearchQuery.value) return routes.value
      
      const query = removeVietnameseAccents(routeSearchQuery.value)
      return routes.value.filter(route => {
        const destination = removeVietnameseAccents(route.destination_location)
        return destination.includes(query)
      })
    })

    const normalizeMonthIndex = monthIndex => ((monthIndex % 12) + 12) % 12
    const monthLabelFormatter = monthIndex => `Tháng ${normalizeMonthIndex(monthIndex) + 1}`

    const monthFilters = computed(() => {
      const filters = []
      for (let offset = 0; offset < 5; offset++) {
        const baseMonthIndex = currentMonth + offset
        const date = new Date(currentYear, baseMonthIndex, 1)
        const label = monthLabelFormatter(baseMonthIndex)
        filters.push({
          label,
          value: ['current', 'next', 'nextnext', 'nextnextnext', 'nextnextnextnext'][offset],
          start: new Date(date.getFullYear(), date.getMonth(), 1),
          end: new Date(date.getFullYear(), date.getMonth() + 1, 0),
          month: date.getMonth() + 1,
          year: date.getFullYear()
        })
      }
      return filters
    })

    const handleMonthFilter = async filterValue => {
      selectedMonthFilter.value = filterValue

      try {
        let params = {}

        // Always use date filters now that 'all' is removed
        const filter = monthFilters.value.find(f => f.value === filterValue)
        if (filter) {
          // Get month and year from the filter dates
          const startDate = new Date(filter.start)
          params = {
            month: startDate.getMonth() + 1, // Adding 1 because getMonth() returns 0-11
            year: startDate.getFullYear()
          }
        }

        // Make API call with the date parameters
        const response = await axiosInstance.get(`${API_BASE_URL}/routes/`, {
          params: { ...params, per_page: 1000 }
        })

        routes.value = response.data

        // Reset selections when filter changes
        selectedRoute.value = null
        selectedTrip.value = null
        selectedSeats.value = []
        trips.value = []
      } catch (error) {
        console.error('Error fetching filtered routes:', error)
      }
    }

    const calculateAddonTotal = () => {
      return selectedAddons.value.reduce((total, addon) => {
        const price = addon.price || getAddonPrice(addon.productId)
        return total + price * addon.quantity
      }, 0)
    }

    const calculateTotalWithAddons = () => {
      const basePrice = calculateTotalPrice.value
      const addonTotal = calculateAddonTotal()
      return basePrice + addonTotal
    }

    watch(
      () => selectedTrip.value?.trip_id,
      newTripId => {
        if (newTripId) {
          fetchHeldSeats()
        } else {
          heldSeats.value = []
        }
      }
    )

    const totalWithAddons = computed(() => {
      const basePrice = calculateTotalPrice.value

      // Tính tổng giá trị các sản phẩm phụ trợ
      const addonTotal = selectedAddons.value.reduce((total, addon) => {
        const product = addonProducts.value.find(
          p => p.addon_id === addon.productId
        )
        return total + (product ? product.price * addon.quantity : 0)
      }, 0)

      return basePrice + addonTotal
    })

    const refreshAvailableSeatCounts = debounce(async () => {
      if (isRefreshingSeats.value || !trips.value.length) return

      try {
        // Hiển thị trạng thái loading ngay lập tức
        isRefreshingSeats.value = true

        // Lấy tất cả các trip_id, không chỉ 10 chuyến đầu tiên
        const allTripIds = trips.value.map(trip => trip.trip_id)

        if (allTripIds.length === 0) {
          isRefreshingSeats.value = false
          return
        }

        const availabilityResponse = await axiosInstance.get(
          `${API_BASE_URL}/trips/availability`,
          {
            params: { trip_ids: allTripIds.join(',') }
          }
        )

        // Cập nhật thông tin chỗ ngồi
        if (
          availabilityResponse.data &&
          Array.isArray(availabilityResponse.data)
        ) {
          availabilityResponse.data.forEach(tripData => {
            const tripIndex = trips.value.findIndex(
              t => t.trip_id === tripData.trip_id
            )
            if (tripIndex >= 0) {
              trips.value[tripIndex].available_seats = tripData.available_seats
            }
          })
        }
      } catch (error) {
        console.error('Error refreshing seat availability:', error)
      } finally {
        // Đảm bảo ẩn trạng thái loading ngay cả khi có lỗi
        isRefreshingSeats.value = false
      }
    }, 50)

    watch(
      () => currentStep.value,
      newStep => {
        // Clear any existing interval
        if (seatRefreshInterval.value) {
          clearInterval(seatRefreshInterval.value)
          seatRefreshInterval.value = null
        }

        // Start interval if on the time selection step (step 1)
        if (newStep === 1) {
          refreshAvailableSeatCounts() // Refresh immediately
          seatRefreshInterval.value = setInterval(
            refreshAvailableSeatCounts,
            30000
          ) // Then every 30 seconds
        }
      }
    )

    const steps = [
      { id: 'location', label: 'Chọn địa điểm' },
      { id: 'time', label: 'Chọn thời gian' },
      { id: 'ticket', label: 'Chọn loại vé' },
      { id: 'seats', label: 'Chọn vị trí ghế' },
      { id: 'addons', label: 'Dịch vụ bổ sung' }, // Bước mới
      { id: 'info', label: 'Thông tin' }
    ]

    // Mảng lưu trạng thái hoàn thành của từng bước
    const completedSteps = ref(steps.map(() => false))

    const visibleTicketTypes = computed(() => {
      return ticketTypes.value.filter(ticket => !ticket.is_hidden)
    })

    const isSpecialTicketType = computed(() => {
      if (!selectedTicketType.value) return false

      const selectedTicket = ticketTypes.value.find(
        t => t.ticket_type_id === selectedTicketType.value
      )

      return selectedTicket && selectedTicket.independent
    })

    const canProceed = computed(() => {
      switch (currentStep.value) {
        case 0:
          return !!selectedRoute.value
        case 1:
          return !!selectedTrip.value
        case 2:
          return !!selectedTicketType.value
        case 3:
          // Special tickets don't need seat selection
          return isSpecialTicketType.value || selectedSeats.value.length > 0
        case 4:
          // For the Addon Services step, we can always proceed
          return true
        case 5: {
          const primaryMember = groupMembers.value[0]
          if (!primaryMember || groupMembers.value.some((member, index) =>
            getCCCDValidationMessage(member.cccd, index === 0)
          )) return false

          // Remove phone/email validation check
          phoneError.value = ''
          emailError.value = ''

          if (isSpecialTicketType.value) {
            // For special tickets, validate ALL members
            // Primary member (person 1): name, phone, cccd, birthYear
            const primaryValid = !!(
              primaryMember.name &&
              primaryMember.phone &&
              primaryMember.birthYear !== null &&
              primaryMember.birthYear !== undefined &&
              primaryMember.birthYear !== ''
            )

            // Additional members (person 2+): name, birthDate, phone, email
            const otherMembersValid = groupMembers.value.slice(1).every(
              member =>
                !!member.name &&
                !!member.birthDate
            )

            return primaryValid && otherMembersValid
          } else {
            // For regular tickets, validate all members
            const allMembersValid = groupMembers.value.every(
              member =>
                !!member.name &&
                member.birthYear !== null &&
                member.birthYear !== undefined &&
                member.birthYear !== ''
            )

            return (
              allMembersValid &&
              !!primaryMember.name &&
              !!primaryMember.phone &&
              !!primaryMember.pickupLocationId
            )
          }
        }
        default:
          return false
      }
    })

    const fetchHeldSeats = throttle(async () => {
      try {
        if (!selectedTrip.value?.trip_id) return

        // Gộp 2 requests thành 1 API call
        const response = await axiosInstance.get(
          `${API_BASE_URL}/trips/held-and-paying-seats`,
          {
            params: { trip_id: selectedTrip.value.trip_id }
          }
        )

        // Giả sử API trả về { heldSeats: [...], payingSeats: [...] }
        if (response.data) {
          const allUnavailableSeats = [
            ...response.data.heldSeats,
            ...response.data.payingSeats
          ]
          heldSeats.value = [...new Set(allUnavailableSeats)]
        }
      } catch (error) {
        console.error('Error fetching held seats:', error)
        heldSeats.value = []
      }
    }, 50)

    const nextStep = async () => {
      if (isProcessing.value.nextStep || !canProceed.value) return

      try {
        isProcessing.value.nextStep = true

        if (currentStep.value < steps.length - 1) {
          if (currentStep.value === 3) {
            // For special tickets, we don't need to hold seats
            if (isSpecialTicketType.value) {
              // (existing special ticket logic)
            } else {
              // Regular tickets - existing seat holding logic
              if (holdTicketInfo.value) {
                // Check if user is selecting different seats than what they're holding
                const isSameSeats = selectedSeats.value.every(seat =>
                  holdTicketInfo.value.slot_numbers.includes(seat.toString())
                )

                if (!isSameSeats) {
                  // Show error and prevent proceeding
                  ToastService.error(
                    'Bạn đã chọn ghế khác so với ghế đang giữ. Vui lòng hoàn tất đơn trước đó hoặc huỷ đơn và chọn lại ghế.'
                  )
                  return // Stop execution and prevent proceeding
                } else {
                  // Seats are the same, keep the hold
                  currentStep.value++
                  return
                }
              }

              // Create holds for the selected seats
              const success = await holdSelectedSeats()
              if (!success) return

              // Update group members based on selected seats
              while (groupMembers.value.length < selectedSeats.value.length) {
                groupMembers.value.push({
                  name: '',
                  phone: '',
                  email: '',
                  birthYear: null,
                  pickupLocationId: ''
                })
              }

              while (
                groupMembers.value.length > selectedSeats.value.length &&
                groupMembers.value.length > 1
              ) {
                groupMembers.value.pop()
              }

              // Reset to the first member tab
              activeMemberIndex.value = 0
            }
          } else if (currentStep.value === 4) {
             // Call reserveAllAddons from AddonServices component
             if (addonServicesRef.value) {
               try {
                 const success = await addonServicesRef.value.reserveAllAddons()
                 if (!success) {
                   // If reservation fails, don't proceed
                   return
                 }
               } catch (error) {
                 console.error('Error calling reserveAllAddons:', error)
                 // Decide whether to block or proceed on unexpected error. blocking is safer.
                 ToastService.error('Có lỗi xảy ra khi xử lý dịch vụ bổ sung')
                 return
               }
             }
          }

          completedSteps.value[currentStep.value] = true
          currentStep.value++
        }
      } finally {
        // Release the button lock after a short delay to prevent double-clicks
        setTimeout(() => {
          isProcessing.value.nextStep = false
        }, 500)
      }
    }

    const previousStep = () => {
      if (currentStep.value > 0) {
        currentStep.value--
      }
    }

    const resetSubsequentSteps = currentStepIndex => {
      // Reset completedSteps array for all steps after current step
      for (let i = currentStepIndex; i < completedSteps.value.length; i++) {
        completedSteps.value[i] = false
      }

      // Reset states based on the step we're at
      if (currentStepIndex <= 0) {
        // Reset everything if on step 0 (location selection)
        selectedRoute.value = null
        selectedTrip.value = null
        selectedTicketType.value = null
        selectedSeats.value = []
        to.value = ''
        trips.value = []
        ticketTypes.value = []
        pickupLocations.value = []

        // Reset hold ticket data if exists
        if (holdTicketInfo.value) {
          cancelHoldTicket()
        }
      } else if (currentStepIndex <= 1) {
        // Reset from trip selection and onwards
        selectedTrip.value = null
        selectedTicketType.value = null
        selectedSeats.value = []
        ticketTypes.value = []
        pickupLocations.value = []

        if (holdTicketInfo.value) {
          cancelHoldTicket()
        }
      } else if (currentStepIndex <= 2) {
        // Reset from ticket type selection and onwards
        selectedTicketType.value = null
        selectedSeats.value = []

        if (holdTicketInfo.value) {
          cancelHoldTicket()
        }
      } else if (currentStepIndex <= 3) {
        // Reset from seat selection and onwards
        selectedSeats.value = []

        // For special tickets, we don't need to release holds
        if (!isSpecialTicketType.value && holdTicketInfo.value) {
          cancelHoldTicket()
        }
      }

      // Reset group members if needed
      if (currentStepIndex <= 3) {
        groupMembers.value = [
          {
            name: '',
            phone: '',
            email: '',
            birthYear: null,
            pickupLocationId: ''
          }
        ]
        activeMemberIndex.value = 0
      }
    }

    const navigateToStep = stepIndex => {
      if (stepIndex === currentStep.value) return

      if (stepIndex > currentStep.value) {
        if (stepIndex === 1 && completedSteps.value[0] === false) return
        if (stepIndex === 2 && completedSteps.value[1] === false) return
        if (stepIndex === 3 && completedSteps.value[2] === false) return
        if (stepIndex === 4 && completedSteps.value[3] === false) return
        if (stepIndex === 5 && completedSteps.value[4] === false) return

        if (
          !(
            completedSteps.value[stepIndex] ||
            stepIndex === currentStep.value + 1
          )
        ) {
          return
        }
      }

      // Allow moving to completed steps or to the next step
      if (
        completedSteps.value[stepIndex] ||
        stepIndex === currentStep.value + 1 ||
        stepIndex < currentStep.value
      ) {
        if (stepIndex === 4) {
          // Ensure groupMembers are synchronized correctly
          if (isSpecialTicketType.value) {
            // For special tickets, use the quantity
            while (groupMembers.value.length < specialTicketQuantity.value) {
              groupMembers.value.push({
                name: '',
                phone: '',
                email: '',
                birthYear: null,
                pickupLocationId: ''
              })
            }

            // If there are more members than the quantity, remove extras
            while (
              groupMembers.value.length > specialTicketQuantity.value &&
              groupMembers.value.length > 1
            ) {
              groupMembers.value.pop()
            }
          } else {
            // For regular tickets, use selected seats
            while (groupMembers.value.length < selectedSeats.value.length) {
              groupMembers.value.push({
                name: '',
                phone: '',
                email: '',
                birthYear: null,
                pickupLocationId: ''
              })
            }

            while (
              groupMembers.value.length > selectedSeats.value.length &&
              groupMembers.value.length > 1
            ) {
              groupMembers.value.pop()
            }
          }

          // Reset to the first member tab
          activeMemberIndex.value = 0
        }

        currentStep.value = stepIndex
      }

      // If selecting step 3, refresh held seats - only for regular tickets
      if (stepIndex === 3 && !isSpecialTicketType.value) {
        fetchHeldSeats()
      }
    }

    const restoreBookingState = async holdData => {
      try {
        // Load only the specific trip data first
        const tripResponse = await axiosInstance.get(`${API_BASE_URL}/trips/`, {
          params: { trip_id: holdData.trip_id }
        })

        if (
          !tripResponse.data ||
          (Array.isArray(tripResponse.data) && tripResponse.data.length === 0)
        ) {
          throw new Error('Trip not found')
        }

        const tripData = Array.isArray(tripResponse.data)
          ? tripResponse.data[0]
          : tripResponse.data

        // Load only this specific route (instead of all routes)
        const routeResponse = await axiosInstance.get(
          `${API_BASE_URL}/routes/${tripData.route_id}`
        )

        // Instead of setting entire routes array, just add this one route
        routes.value = [routeResponse.data]

        // Load ticket types for this specific trip
        const ticketTypesResponse = await axiosInstance.get(
          `${API_BASE_URL}/ticket_types/`,
          {
            params: { trip_id: holdData.trip_id, per_page: 1000 }
          }
        )
        ticketTypes.value = ticketTypesResponse.data || []

        // Load only bookings for this trip
        const bookingsResponse = await axiosInstance.get(
          `${API_BASE_URL}/bookings/`,
          {
            params: { trip_id: holdData.trip_id, per_page: 1000 }
          }
        )
        bookings.value = bookingsResponse.data

        // Set up data for this specific trip
        selectedRoute.value = tripData.route_id
        to.value = holdData.destination
        selectedTrip.value = tripData
        selectedSeats.value = holdData.slot_numbers

        // Load pickup locations only for this trip
        await fetchPickupLocations(holdData.trip_id)

        // If specific route endpoint isn't available, create an alternative
        if (routeResponse.status !== 200) {
          // Just add minimal route info based on trip data
          routes.value = [
            {
              route_id: tripData.route_id,
              destination_location: holdData.destination || 'Unknown',
              price: tripData.price || 0
            }
          ]
        }

        // Update completed steps
        completedSteps.value = [true, true, true, true, false]
      } catch (error) {
        console.error('Error restoring booking state:', error)
        secureLocalStorage.removeItem('holdTicket')
        holdTicketInfo.value = null
        ToastService.error(
          'Không thể khôi phục thông tin đặt vé. Vui lòng đặt lại.'
        )

        // Load routes for current month as fallback
        await handleMonthFilter('current')
      }
    }

    const route = useRoute()

    const getMonthFilterValue = month => {
      const targetMonth = parseInt(month, 10)
      if (Number.isNaN(targetMonth)) return 'current'

      const matchingFilter = monthFilters.value.find(
        filter => filter.month === targetMonth
      )

      return matchingFilter ? matchingFilter.value : 'current'
    }

    onMounted(async () => {
      try {
        // Show loading animation
        isInitialLoading.value = true

        // Read search parameter from URL
        const urlParams = new URLSearchParams(window.location.search)
        const searchParam = urlParams.get('search')
        if (searchParam) {
          routeSearchQuery.value = searchParam
          isSearching.value = true
        }

        // Check for month parameter
        const monthParam = route.query.month || urlParams.get('month')
        let initialMonthFilter = 'current'
        
        if (monthParam) {
          const monthValue = getMonthFilterValue(monthParam)
          initialMonthFilter = monthValue
          selectedMonthFilter.value = monthValue
        }

        // Check if there's a held ticket
        const storedHold = secureLocalStorage.getItem('holdTicket')

        if (storedHold) {
          const holdData = storedHold
          const isValid = await checkAndStartHoldTimer(storedHold)

          if (isValid) {
            holdTicketInfo.value = holdData
            // Restore only the specific information for this ticket
            // await restoreBookingState(holdData)
            await handleMonthFilter(initialMonthFilter)
          } else {
            secureLocalStorage.removeItem('holdTicket')
            await handleMonthFilter(initialMonthFilter)
          }
        } else {
          // Load routes for the specified month or current month
          await handleMonthFilter(initialMonthFilter)
        }
      } catch (error) {
        console.error('Error fetching initial data:', error)
      } finally {
        // Show the bus animation for at least 2.5 seconds
        setTimeout(() => {
          isInitialLoading.value = false
          isSearching.value = false
        }, 2500)
      }
    })

    // Cleanup on component unmount
    onUnmounted(() => {
      if (holdTimer.value) {
        clearInterval(holdTimer.value)
      }
      if (seatRefreshInterval.value) {
        clearInterval(seatRefreshInterval.value)
      }
    })

    const currentRoute = computed(() =>
      routes.value.find(r => r.route_id === selectedRoute.value)
    )

    const filteredTrips = computed(() =>
      trips.value.filter(trip => trip.route_id === selectedRoute.value)
    )
    const sortedTrips = computed(() => {
      const tripsToSort = [...filteredTrips.value]

      return tripsToSort.sort((a, b) => {
        const dateA = new Date(a.departure_datetime).getTime()
        const dateB = new Date(b.departure_datetime).getTime()

        return sortOption.value === 'asc' ? dateA - dateB : dateB - dateA
      })
    })

    const seatGrid = computed(() => {
      if (!selectedTrip.value || !selectedTrip.value.vehicle) {
        return []
      }

      const vehicle = selectedTrip.value.vehicle
      let totalSeats = 28 // default value

      switch (vehicle.type) {
        case '16_slots':
          totalSeats = 16
          break
        case '45_slots':
          totalSeats = 45
          break
        case 'over_night':
          totalSeats = 46
          break
      }

      const rows = []
      let currentRow = []

      for (let i = 1; i <= totalSeats; i++) {
        currentRow.push({ id: i })
        if (currentRow.length === 4 || i === totalSeats) {
          while (currentRow.length < 4) {
            currentRow.push({ id: null })
          }
          rows.push([...currentRow])
          currentRow = []
        }
      }

      return rows
    })

    // Statuses that release their seat (must match backend SEAT_RELEASING_STATUSES)
    const SEAT_RELEASING_STATUSES = [
      'canceled', 'refund', 'exchange', 'reserved',
      'refund_90', 'refund_80', 'refund_70', 'refund_50', 'no_refund'
    ]

    const getBookedSeats = computed(() => {
      if (!selectedTrip.value) return []

      return bookings.value
        .filter(
          booking =>
            booking.trip_id === selectedTrip.value.trip_id &&
            !SEAT_RELEASING_STATUSES.includes(booking.status)
        )
        .map(booking => booking.slot_number.toString())
    })

    const getSelectedTicketTypeName = computed(() => {
      const type = ticketTypes.value.find(
        t => t.ticket_type_id === selectedTicketType.value
      )
      return type ? type.type : ''
    })

    const calculateTotalPrice = computed(() => {
      if (!selectedTicketType.value) return 0

      const ticketType = ticketTypes.value.find(
        t => t.ticket_type_id === selectedTicketType.value
      )

      if (!ticketType) return 0

      // For special tickets, use quantity instead of selected seats
      if (isSpecialTicketType.value) {
        return ticketType.price * specialTicketQuantity.value
      }

      // For regular tickets, use selected seats length
      return ticketType.price * selectedSeats.value.length
    })

    const canBook = computed(
      () =>
        selectedSeats.value.length > 0 &&
        groupMembers.value[0].name &&
        groupMembers.value[0].phone
    )

    const getAddonName = productId => {
      const addon = addonProducts.value.find(p => p.addon_id === productId)
      return addon ? addon.name : ''
    }

    const getAddonPrice = productId => {
      const addon = addonProducts.value.find(p => p.addon_id === productId)
      return addon ? addon.price : 0
    }

    const handleTicketTypeSelect = type => {
      try {
        if (!type || !type.ticket_type_id) {
          console.error('Invalid ticket type:', type)
          return
        }

        const oldTicketType = selectedTicketType.value

        // If ticket type changed
        if (oldTicketType !== type.ticket_type_id) {
          // Reset subsequent steps
          resetSubsequentSteps(2)
        }

        selectedTicketType.value = type.ticket_type_id
        if (selectedTicketType.value) {
          completedSteps.value[2] = true
          nextStep()
        }
      } catch (error) {
        console.error('Error selecting ticket type:', error)
      }
    }

    const handleRouteSelect = async (
      routeId,
      destinationLocation,
      skipNavigation = false,
      event
    ) => {
      if (isProcessing.value.fetchingTrips) return

      const clickedElement = event ? event.currentTarget : null
      if (clickedElement) clickedElement.classList.add('button-clicked')

      try {
        isProcessing.value.fetchingTrips = true

        // Update UI immediately
        selectedRoute.value = routeId
        to.value = destinationLocation

        if (!skipNavigation) {
          completedSteps.value[0] = true

          // Trigger navigation first for better perceived performance
          currentStep.value = currentStep.value + 1
        }

        // Reset data if route changed
        if (selectedRoute.value !== routeId) {
          resetSubsequentSteps(0)
        }

        // Fetch data in background (don't await)
        fetchTrips(routeId, sortOption.value).then(() => {
          // This runs after trips are loaded
          refreshAvailableSeatCounts()
        })
      } catch (error) {
        console.error('Error in route selection:', error)
      } finally {
        // Remove button effect and loading state
        if (clickedElement) {
          setTimeout(() => {
            clickedElement.classList.remove('button-clicked')
          }, 50)
        }
      }
    }

    const handleTripSelect = async (trip, skipNavigation = false, event) => {
      try {
        // Hiển thị trạng thái loading ngay lập tức
        const clickedElement = event ? event.currentTarget : null
        if (clickedElement) clickedElement.classList.add('button-clicked')

        isProcessing.value.fetchingTrips = true

        if (selectedTrip.value?.trip_id !== trip.trip_id) {
          resetSubsequentSteps(1)
        }

        selectedTrip.value = trip
        selectedSeats.value = []

        // Để browser render UI trước khi thực hiện các tác vụ nặng
        await nextTick()

        // Thực hiện các API call song song
        const [tripDetailsPromise, bookingsPromise, ticketTypesPromise] =
          await Promise.allSettled([
            !trip.vehicle
              ? axiosInstance.get(`${API_BASE_URL}/trips/`, {
                  params: { trip_id: trip.trip_id }
                })
              : Promise.resolve({ data: trip }),

            axiosInstance.get(`${API_BASE_URL}/bookings/`, {
              params: { trip_id: trip.trip_id, per_page: 1000 }
            }),

            axiosInstance.get(`${API_BASE_URL}/ticket_types/`, {
              params: { trip_id: trip.trip_id, per_page: 1000 }
            })
          ])

        // Xử lý kết quả
        if (
          tripDetailsPromise.status === 'fulfilled' &&
          tripDetailsPromise.value.data
        ) {
          if (Array.isArray(tripDetailsPromise.value.data)) {
            selectedTrip.value = tripDetailsPromise.value.data[0]
          } else {
            selectedTrip.value = tripDetailsPromise.value.data
          }
        }

        if (
          bookingsPromise.status === 'fulfilled' &&
          bookingsPromise.value.data
        ) {
          bookings.value = bookingsPromise.value.data
        }

        if (
          ticketTypesPromise.status === 'fulfilled' &&
          ticketTypesPromise.value.data
        ) {
          ticketTypes.value = ticketTypesPromise.value.data
        }

        // Tìm nạp ghế và chỉ định điểm đón
        if (selectedTrip.value) {
          fetchPickupLocations(selectedTrip.value.trip_id)
          fetchHeldSeats()

          // Nếu không phải skipNavigation, di chuyển đến bước tiếp theo
          if (!skipNavigation) {
            completedSteps.value[1] = true
            nextStep()
          }
        }
      } catch (error) {
        console.error('Error fetching trip details:', error)
      } finally {
        isProcessing.value.fetchingTrips = false
        // if (clickedElement) {
        //   setTimeout(() => {
        //     clickedElement.classList.remove('button-clicked')
        //   }, 300)
        // }
      }
    }

    const handleSeatSelect = async seatId => {
      if (!seatId || isBooked(seatId)) return

      // Check if the seat is already held by the user
      const isCurrentlyHeld =
        holdTicketInfo.value?.slot_numbers.includes(seatId)

      // Case 1: Trying to deselect a held seat
      if (isCurrentlyHeld && selectedSeats.value.includes(seatId)) {
        try {
          await axiosInstance.delete(`${API_BASE_URL}/hold_slots/`, {
            data: {
              trip_id: selectedTrip.value.trip_id,
              slot_number: seatId
            }
          })

          selectedSeats.value = selectedSeats.value.filter(
            seat => seat !== seatId
          )
          if (holdTicketInfo.value) {
            holdTicketInfo.value.slot_numbers =
              holdTicketInfo.value.slot_numbers.filter(seat => seat !== seatId)

            if (holdTicketInfo.value.slot_numbers.length === 0) {
              holdTicketInfo.value = null
              secureLocalStorage.removeItem('holdTicket')
              if (holdTimer.value) {
                clearInterval(holdTimer.value)
                holdTimer.value = null
              }
            } else {
              secureLocalStorage.setItem(
                'holdTicket',
                JSON.stringify(holdTicketInfo.value)
              )
            }
          }
        } catch (error) {
          console.error('Error releasing seat:', error)
        }
        return
      }

      // Case 2: Normal seat selection
      const index = selectedSeats.value.indexOf(seatId)
      if (index > -1) {
        selectedSeats.value.splice(index, 1)
      } else {
        selectedSeats.value.push(seatId)
      }
    }

    const holdSelectedSeats = async () => {
      if (isProcessing.value.holdSelectedSeats) return false
      try {
        isProcessing.value.holdSelectedSeats = true
        // Trước khi gửi yêu cầu giữ ghế, làm mới danh sách ghế đã đặt
        await fetchHeldSeats()

        // Kiểm tra xem ghế đã được chọn có đang được giữ hoặc đã đặt không
        const currentlyUnavailableSeats = [
          ...getBookedSeats.value,
          ...heldSeats.value
        ]
        const potentialConflicts = selectedSeats.value.filter(seatId =>
          currentlyUnavailableSeats.includes(seatId)
        )

        // Nếu có ghế không khả dụng, thông báo và không tiếp tục
        if (potentialConflicts.length > 0) {
          ToastService.error(
            `Ghế ${potentialConflicts.join(
              ', '
            )} đã được đặt hoặc đang được giữ. Vui lòng chọn ghế khác.`
          )
          selectedSeats.value = selectedSeats.value.filter(
            seat => !potentialConflicts.includes(seat)
          )
          return false
        }

        let holdUntilIso = null
        if (CHECKOUT_V2_ENABLED) {
          try {
            await vueFlow.start(selectedTrip.value.trip_id, selectedTicketType.value)
            const { view } = await vueFlow.chooseSeats(selectedSeats.value.map(String), {
              quantity: selectedSeats.value.length
            })
            holdUntilIso = view.expires_at || null
            rememberCheckout(vueFlow.checkoutId)
            if (!holdUntilIso) {
              ToastService.error('Máy chủ chưa xác nhận giữ chỗ. Vui lòng thử lại.')
              return false
            }
          } catch (error) {
            if (error instanceof SelectionError && error.conflict) {
              const dropped = error.dropped || []
              ToastService.error(
                `${error.message}${dropped.length ? ' Ghế ' + dropped.join(', ') + ' đã bị bỏ khỏi lựa chọn.' : ''}`
              )
              selectedSeats.value = selectedSeats.value.filter(seat => !dropped.includes(seat))
              await fetchHeldSeats()
              return false
            }
            ToastService.error(error?.message || 'Không giữ được chỗ. Vui lòng thử lại.')
            return false
          }
        } else {
          // Create new holds for currently selected seats
          const holdPromises = selectedSeats.value.map(async seatId => {
            try {
              const response = await axiosInstance.post(
                `${API_BASE_URL}/hold_slots/`,
                {
                  trip_id: selectedTrip.value.trip_id,
                  slot_number: seatId.toString(),
                  hold_until: new Date(Date.now() + 9 * 60 * 1000).toISOString()
                }
              )
              return {
                success: true,
                seatId,
                holdUntil: response.data.hold_until
              }
            } catch (error) {
              // Xem xét chi tiết lỗi để cung cấp thông báo phù hợp
              console.error(`Error holding seat ${seatId}:`, error)
              const errorMessage =
                error.response?.status === 409
                  ? 'đã được đặt bởi người khác'
                  : 'không thể giữ do lỗi hệ thống'

              return {
                success: false,
                seatId,
                error,
                errorMessage
              }
            }
          })

          const results = await Promise.all(holdPromises)
          const failures = results.filter(r => !r.success)

          if (failures.length > 0) {
            // Some seats failed to be held
            const failedSeats = failures.map(
              f => `${f.seatId} (${f.errorMessage || 'lỗi không xác định'})`
            )
            selectedSeats.value = selectedSeats.value.filter(
              seat => !failures.map(f => f.seatId).includes(seat)
            )

            // Cập nhật lại danh sách ghế đã đặt
            await fetchHeldSeats()

            ToastService.error(
              `Không thể giữ (các) ghế: ${failedSeats.join(
                ', '
              )}. Vui lòng chọn ghế khác.`
            )
            return false
          }
          // Get the earliest hold expiration time
          const earliestHoldUntil = results.reduce((earliest, current) => {
            const currentTime = new Date(current.holdUntil).getTime()
            return earliest ? Math.min(earliest, currentTime) : currentTime
          }, null)
          holdUntilIso = new Date(earliestHoldUntil).toISOString()
        }
        // Get current ticket type info
        const currentTicketType = ticketTypes.value.find(
          t => t.ticket_type_id === selectedTicketType.value
        )

        // Store hold data with ticket type
        const holdData = {
          trip_id: selectedTrip.value.trip_id,
          slot_numbers: selectedSeats.value.map(String),
          holdUntil: holdUntilIso,
          ticketType: currentTicketType,
          groupMembers: groupMembers.value, // Add this line to save group members
          destination: to.value,
          departure_datetime: selectedTrip.value.departure_datetime
        }

        secureLocalStorage.setItem('holdTicket', holdData)
        holdTicketInfo.value = {
          ...holdData
        }

        startHoldTimer(holdData.holdUntil)

        return true
      } catch (error) {
        console.error('Error holding seats:', error)
        ToastService.error('Có lỗi xảy ra khi giữ ghế. Vui lòng thử lại.')
        return false
      }
    }

    // Add confirm hold ticket function
    // Update the confirmHoldTicket function to preserve the correct ticket type
    const confirmHoldTicket = async () => {
      if (isProcessing.value.confirmHoldTicket) return

      try {
        isProcessing.value.confirmHoldTicket = true

        // Check if holdTicketInfo exists
        if (!holdTicketInfo.value) {
          ToastService.error('Không tìm thấy thông tin vé đã giữ.')
          secureLocalStorage.removeItem('holdTicket')
          return
        }

        const holdData = holdTicketInfo.value

        // Validate required data
        if (!holdData.trip_id) {
          ToastService.error('Thông tin vé không hợp lệ.')
          secureLocalStorage.removeItem('holdTicket')
          holdTicketInfo.value = null
          return
        }

        // Run API requests in parallel instead of sequentially
        const [tripResponse, ticketTypesResponse, bookingsResponse] =
          await Promise.all([
            // Get only the specific trip data
            axiosInstance.get(`${API_BASE_URL}/trips/`, {
              params: { trip_id: holdData.trip_id }
            }),

            // Get ticket types for this trip
            axiosInstance.get(`${API_BASE_URL}/ticket_types/`, {
              params: { trip_id: holdData.trip_id, per_page: 1000 }
            }),

            // Get bookings for this trip
            axiosInstance.get(`${API_BASE_URL}/bookings/`, {
              params: { trip_id: holdData.trip_id, per_page: 1000 }
            })
          ])

        // Handle potential empty response
        if (
          !tripResponse.data ||
          (Array.isArray(tripResponse.data) && tripResponse.data.length === 0)
        ) {
          ToastService.error('Không tìm thấy thông tin chuyến đi.')
          return
        }

        // Process trip data
        const tripData = Array.isArray(tripResponse.data)
          ? tripResponse.data[0]
          : tripResponse.data

        // Store essential data
        ticketTypes.value = ticketTypesResponse.data || []
        bookings.value = bookingsResponse.data || []
        selectedTrip.value = tripData
        selectedRoute.value = tripData.route_id
        to.value = holdData.destination || ''
        selectedSeats.value = holdData.slot_numbers || []
        trips.value = [tripData]

        // Use stored ticket type with better null checking
        if (holdData.ticketType && holdData.ticketType.ticket_type_id) {
          // First check if this ticket type exists in current types
          const matchingType = ticketTypes.value.find(
            t => t.ticket_type_id === holdData.ticketType.ticket_type_id
          )
          selectedTicketType.value = matchingType
            ? matchingType.ticket_type_id
            : ticketTypes.value.length > 0
            ? ticketTypes.value[0].ticket_type_id
            : null
        } else if (ticketTypes.value.length > 0) {
          // Fallback to first appropriate ticket type
          const regularTicket = ticketTypes.value.find(t => !t.independent)
          selectedTicketType.value = regularTicket
            ? regularTicket.ticket_type_id
            : ticketTypes.value[0].ticket_type_id
        }

        // Restore customer info if available
        if (holdData.groupMembers && holdData.groupMembers.length > 0) {
          groupMembers.value = holdData.groupMembers
        }

        // Fetch pickup locations in the background
        fetchPickupLocations(holdData.trip_id)

        // Update completed steps
        completedSteps.value = [true, true, true, true, false]

        // Navigate to appropriate step
        if (groupMembers.value[0].name && groupMembers.value[0].phone) {
          currentStep.value = 4 // Customer info step
          ToastService.success('Tiếp tục đặt vé')
        } else if (selectedSeats.value.length > 0) {
          currentStep.value = 4 // Go to customer info
          ToastService.success('Vui lòng điền thông tin để hoàn tất đặt vé')
        } else {
          currentStep.value = 3 // Go to seat selection
          ToastService.info('Vui lòng chọn vị trí ghế')
        }
      } catch (error) {
        console.error('Error restoring booking state:', error)
        ToastService.error('Có lỗi xảy ra khi xử lý vé. Vui lòng thử lại sau.')

        // Clean up data in case of error
        selectedSeats.value = []
        secureLocalStorage.removeItem('holdTicket')
        holdTicketInfo.value = null
      } finally {
        // Always reset processing flag after brief delay
        setTimeout(() => {
          isProcessing.value.confirmHoldTicket = false
        }, 300)
      }
    }

    const isBooked = seatId => {
      return bookings.value.some(
        booking =>
          booking.slot_number === seatId &&
          booking.trip_id === selectedTrip.value.trip_id &&
          !SEAT_RELEASING_STATUSES.includes(booking.status)
      )
    }

    const getAvailableSeats = trip => {
      // Ưu tiên sử dụng dữ liệu từ API trips/availability nếu có
      if (trip && trip.available_seats !== undefined) {
        return trip.available_seats
      }

      // Fallback sang phương pháp tính thủ công nếu không có dữ liệu từ API
      if (!trip || !trip.vehicle || !trip.vehicle.type) {
        return 0
      }

      const totalSeats =
        {
          '16_slots': 16,
          '28_slots': 28,
          '45_slots': 45,
          over_night: 46
        }[trip.vehicle.type] || 28

      const tripBookings = bookings.value.filter(
        b =>
          b.trip_id === trip.trip_id &&
          !SEAT_RELEASING_STATUSES.includes(b.status) &&
          b.status !== 'paying' &&
          b.slot_number !== 'special' &&
          !(b.ticket_type && b.ticket_type.independent)
      )

      // Count locked seats (for staff/driver) if they exist
      const lockedSeatsCount =
        trip.locked_seats && Array.isArray(trip.locked_seats)
          ? trip.locked_seats.length
          : 0

      return totalSeats - tripBookings.length - lockedSeatsCount
    }

    const formatDateTime = datetime => {
      const date = new Date(datetime)
      return date.toLocaleDateString('vi-VN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const getValidationMessage = () => {
      // First check if we're in the right step - only validate at step 4
      if (currentStep.value < 4) {
        return 'Vui lòng hoàn thành các bước trước khi đặt vé.'
      }

      const messages = []

      // Route validation
      if (!selectedRoute.value) {
        messages.push('Chọn tuyến đường')
      }

      // Trip validation
      if (!selectedTrip.value) {
        messages.push('Chọn chuyến đi')
      }

      // Ticket type validation
      if (!selectedTicketType.value) {
        messages.push('Chọn loại vé')
      }

      // Seat validation (only for non-special tickets)
      if (!isSpecialTicketType.value && selectedSeats.value.length === 0) {
        messages.push('Chọn ghế ngồi')
      }

      groupMembers.value.forEach((member, index) => {
        const message = getCCCDValidationMessage(member.cccd, index === 0)
        if (message) messages.push(`${message} (hành khách ${index + 1})`)
      })

      // Customer info validation
      if (isSpecialTicketType.value) {
        // For special tickets, validate all passengers
        for (let i = 0; i < groupMembers.value.length; i++) {
          const member = groupMembers.value[i]
          if (!member.name) {
            messages.push(`Họ và tên hành khách ${i + 1}`)
          }

          if (i === 0) {
            // Leader validations
            if (!member.phone) messages.push(`Số điện thoại hành khách ${i + 1}`)
            if (!member.email) messages.push(`Email hành khách ${i + 1}`)
            if (!member.birthYear) messages.push(`Năm sinh hành khách ${i + 1}`)
            if (!member.cccdAddress) messages.push(`Địa chỉ CCCD hành khách ${i + 1}`)
          } else {
            // Member validations (person 2+): name, DOB, phone, email
            if (!member.birthDate) messages.push(`Ngày sinh hành khách ${i + 1}`)
            if (!member.phone) messages.push(`Số điện thoại hành khách ${i + 1}`)
            if (!member.email) messages.push(`Email hành khách ${i + 1}`)
          }
          // Special tickets don't need pickup location
        }
      } else {
        // For regular tickets, validate all passengers
        for (let i = 0; i < groupMembers.value.length; i++) {
          const member = groupMembers.value[i]
          if (!member.name) {
            messages.push(`Họ và tên hành khách ${i + 1}`)
          }

          if (i === 0) {
            // Leader validations
            if (!member.phone) messages.push(`Số điện thoại hành khách ${i + 1}`)
            if (!member.email) messages.push(`Email hành khách ${i + 1}`)
            if (!member.birthYear) messages.push(`Năm sinh hành khách ${i + 1}`)
            if (!member.cccdAddress) messages.push(`Địa chỉ CCCD hành khách ${i + 1}`)
          } else {
            // Member validations
            if (!member.birthDate) messages.push(`Ngày sinh hành khách ${i + 1}`)
          }
          if (!member.pickupLocationId) {
            messages.push(`Điểm đón hành khách ${i + 1}`)
          }
        }
      }

      return messages.length > 0 ? `Vui lòng điền: ${messages.join(', ')}` : ''
    }

    const fetchPickupLocations = async tripId => {
      try {
        const response = await axiosInstance.get(
          `${API_BASE_URL}/pickup_locations/`,
          {
            params: { trip_id: tripId, per_page: 1000 }
          }
        )
        pickupLocations.value = response.data
      } catch (error) {
        console.error('Error fetching pickup locations:', error)
      }
    }

    const fetchAddonProducts = async () => {
      try {
        const response = await axiosInstance.get(
          `${API_BASE_URL}/addon-products/`
        )
        addonProducts.value = response.data
      } catch (error) {
        console.error('Error fetching addon products:', error)
      }
    }

    const confirmBooking = async () => {
      if (isProcessing.value.confirmBooking) return

      const validationErrors = getValidationMessage()
      if (validationErrors) {
        const invalidMemberIndex = groupMembers.value.findIndex((member, index) =>
          getCCCDValidationMessage(member.cccd, index === 0)
        )
        if (invalidMemberIndex !== -1) {
          currentStep.value = 5
          switchToMember(invalidMemberIndex)
          await nextTick()
          document.getElementById('cccd')?.focus()
        }
        ToastService.error(validationErrors)
        return
      }

      try {
        isProcessing.value.confirmBooking = true

        // Your existing confirmBooking logic
        if (selectedTicketType.value) {
          const ticketType = ticketTypes.value.find(
            t => t.ticket_type_id === selectedTicketType.value
          )

          if (ticketType && ticketType.is_hidden) {
            ToastService.error(
              'Loại vé này đã bị ẩn và không thể đặt. Vui lòng chọn loại vé khác.'
            )
            return
          }
        }

        if (!selectedRoute.value || !selectedTrip.value) {
          return
        }
        //generate a unique booking group ID if number of group members is greater than 1
        const bookingGroupId =
          groupMembers.value.length > 1 ? crypto.randomUUID() : null

        if (isSpecialTicketType.value) {
          // Create array to hold booking promises
          const bookingPromises = []

          if (!groupMembers.value[0].name || !groupMembers.value[0].phone) {
            ToastService.warning('Vui lòng nhập đầy đủ thông tin hành khách.')
            return
          }

          for (let i = 0; i < specialTicketQuantity.value; i++) {
            const member = groupMembers.value[i] || groupMembers.value[0]
            const bookingData = {
              trip_id: selectedTrip.value.trip_id,
              slot_number: 'special', // No seat assignment for special tickets
              customer_name: member.name,
              customer_phone: member.phone || groupMembers.value[0].phone,
              customer_email: member.email || groupMembers.value[0].email || null,
              birth_year: member.birthYear || null,
              pickup_location_id: null,
              ticket_type_id: selectedTicketType.value,
              booking_datetime: new Date().toISOString(),
              status: 'paying',
              type_ticket: getSelectedTicketTypeName.value,
              booking_group_id: bookingGroupId,
              gender: member.gender || null,
              cccd: member.cccd || null,
              cccd_address: member.cccdAddress || null,
              birth_date: i === 0
                ? (member.birthDay && member.birthMonth && member.birthYear
                  ? `${member.birthYear}-${String(member.birthMonth).padStart(2, '0')}-${String(member.birthDay).padStart(2, '0')}`
                  : null)
                : (member.birthDate || null),
              notes: member.notes ? member.notes.trim() : null
            }

            // Add booking promise to array
            bookingPromises.push(
              axiosInstance.post(`${API_BASE_URL}/bookings/`, bookingData)
            )
          }

          // Wait for all bookings to complete
          const responses = await Promise.all(bookingPromises)
          const bookingResults = responses.map(response => response.data)

          if (selectedAddons.value.length > 0) {
            const addonPromises = []

            // For each booking
            for (const booking of bookingResults) {
              // For each selected addon
              for (const addon of selectedAddons.value) {
                // Create booking_addon record
                const bookingAddonData = {
                  booking_id: booking.booking_id,
                  addon_id: addon.productId,
                  quantity: addon.quantity,
                  price: getAddonPrice(addon.productId)
                }

                // Add promise to array
                addonPromises.push(
                  axiosInstance
                    .post(`${API_BASE_URL}/booking-addons/`, bookingAddonData)
                    .catch(error => {
                      console.error('Error creating addon:', error)
                      throw new Error(
                        `Failed to create addon: ${error.message}`
                      )
                    })
                )
              }
            }

            // Wait for all addon records to be created
            try {
              await Promise.all(addonPromises)
            } catch (error) {
              console.error('Error in addon creation:', error)
              ToastService.error('Có lỗi khi tạo dịch vụ bổ sung')
            }
          }

          // Get booking IDs for token
          const bookingIds = bookingResults.map(booking => booking.booking_id)
          const encryptedToken = createBookingToken(bookingIds)

          const paymentInfo = {
            bookings: bookingResults,
            bookingGroupId: bookingGroupId,
            totalAmount: calculateTotalPrice.value,
            addons: selectedAddons.value.map(addon => ({
              addon_id: addon.productId,
              quantity: addon.quantity,
              price: getAddonPrice(addon.productId),
              name: getAddonName(addon.productId),
              total_price: getAddonPrice(addon.productId) * addon.quantity
            })),
            addonsTotal: totalWithAddons.value - calculateTotalPrice.value,
            grandTotal: totalWithAddons.value,
            customerInfo: groupMembers.value[0],
            groupMembers: groupMembers.value.slice(0, specialTicketQuantity.value),
            quantity: specialTicketQuantity.value,
            tripInfo: {
              destination: to.value,
              departure: selectedTrip.value.departure_datetime,
              ticketType: getSelectedTicketTypeName.value
            }
          }

          // Save to secureLocalStorage and navigate to payment page
          secureLocalStorage.setItem('pendingPayment', paymentInfo)
          router.push({
            name: 'PaymentView',
            params: { token: encryptedToken }
          })
          return
        } else {
          if (groupMembers.value.length !== selectedSeats.value.length) {
            ToastService.warning(
              `Vui lòng nhập thông tin cho tất cả ${selectedSeats.value.length} hành khách.`
            )
            return
          }
          // Create an array of booking promises
          const bookingPromises = selectedSeats.value.map(
            async (seatId, index) => {
              const memberInfo =
                groupMembers.value[index] || groupMembers.value[0]

              const isLeader = index === 0
              const bookingData = {
                trip_id: selectedTrip.value.trip_id,
                slot_number: seatId.toString(),
                customer_name: memberInfo.name,
                customer_phone: memberInfo.phone || groupMembers.value[0].phone,
                customer_email: memberInfo.email || groupMembers.value[0].email,
                birth_year: memberInfo.birthYear || null,
                pickup_location_id: memberInfo.pickupLocationId,
                ticket_type_id: selectedTicketType.value,
                booking_datetime: new Date().toISOString(),
                status: 'paying',
                type_ticket: getSelectedTicketTypeName.value,
                booking_group_id: bookingGroupId,
                gender: memberInfo.gender || null,
                cccd: isLeader ? (memberInfo.cccd || null) : null,
                cccd_address: isLeader ? (memberInfo.cccdAddress || null) : null,
                birth_date: isLeader
                  ? (memberInfo.birthDay && memberInfo.birthMonth && memberInfo.birthYear
                    ? `${memberInfo.birthYear}-${String(memberInfo.birthMonth).padStart(2, '0')}-${String(memberInfo.birthDay).padStart(2, '0')}`
                    : null)
                  : (memberInfo.birthDate || null),
                notes: memberInfo.notes ? memberInfo.notes.trim() : null
              }

              try {
                const response = await axiosInstance.post(
                  `${API_BASE_URL}/bookings/`,
                  bookingData
                )
                return response.data
              } catch (err) {
                err.seatId = seatId
                throw err
              }
            }
          )

          const bookingResults = await Promise.all(bookingPromises)

          if (selectedAddons.value.length > 0) {
            const addonPromises = []

            let firstBookingId = bookingResults[0].booking_id
            for (const addon of selectedAddons.value) {
              // Tạo booking_addon record
              const bookingAddonData = {
                booking_id: firstBookingId,
                addon_id: addon.productId,
                quantity: addon.quantity,
                price: getAddonPrice(addon.productId)
              }

              // Thêm promise vào mảng
              addonPromises.push(
                axiosInstance
                  .post(`${API_BASE_URL}/booking-addons/`, bookingAddonData)
                  .catch(error => {
                    console.error('Error creating addon:', error)
                    throw new Error(`Failed to create addon: ${error.message}`)
                  })
              )
            }

            try {
              await Promise.all(addonPromises)
            } catch (error) {
              console.error('Error in addon creation:', error)
              ToastService.error('Có lỗi khi tạo dịch vụ bổ sung')
            }
          }

          // Mã hóa booking IDs
          const bookingIds = bookingResults.map(b => b.booking_id)
          const encryptedToken = createBookingToken(bookingIds)

          const paymentInfo = {
            bookings: bookingResults,
            totalAmount: calculateTotalPrice.value,
            customerInfo: groupMembers.value[0], // Use primary customer info
            groupMembers: groupMembers.value, // Store all group members
            tripInfo: {
              destination: to.value,
              departure: selectedTrip.value.departure_datetime,
              ticketType: getSelectedTicketTypeName.value
            }
          }

          // Lưu vào secureLocalStorage đã mã hóa
          secureLocalStorage.setItem('pendingPayment', paymentInfo)

          // Release held seats
          if (holdTicketInfo.value) {
            const releasePromises = selectedSeats.value.map(seatId =>
              axiosInstance.delete(`${API_BASE_URL}/hold_slots/`, {
                data: {
                  trip_id: selectedTrip.value.trip_id,
                  slot_number: seatId
                }
              })
            )
            await Promise.all(releasePromises)
            secureLocalStorage.removeItem('holdTicket')
            holdTicketInfo.value = null
          }

          // Navigate to payment page
          router.push({
            name: 'PaymentView',
            params: { token: encryptedToken }
          })
        }
      } catch (error) {
        console.error('Error creating booking:', error)

        if (!isSpecialTicketType.value) {
          const releasePromises = selectedSeats.value.map(seatId =>
            axiosInstance.delete(`${API_BASE_URL}/hold_slots/`, {
              data: {
                trip_id: selectedTrip.value.trip_id,
                slot_number: seatId
              }
            }).catch(e => console.warn('Failed to release hold for seat:', seatId, e))
          )
          
          // Don't await release in critical path if not strictly necessary for error display, 
          // or use Promise.allSettled to ensure we don't crash on one failure
          await Promise.allSettled(releasePromises)
        }

        secureLocalStorage.removeItem('holdTicket')
        holdTicketInfo.value = null

        // location.reload()

        // Provide more specific error messages
        if (error.response) {
          switch (error.response.status) {
            case 400:
              ToastService.error(
                'Thông tin đặt vé không hợp lệ. Vui lòng kiểm tra lại.'
              )
              break
            case 409:
              // Conflict - likely seat already booked
              ToastService.warning(
                `Ghế ${error.seatId || ''} đã được đặt. Vui lòng chọn ghế khác.`
              )
              // Refresh seat availability
              fetchHeldSeats()
              break
            case 403:
              ToastService.warning('Phiên đặt vé đã hết hạn. Vui lòng thử lại.')
              break
            case 500:
              ToastService.error('Lỗi máy chủ. Vui lòng thử lại sau.')
              break
            default:
              ToastService.error('Có lỗi xảy ra khi đặt vé. Vui lòng thử lại.')
          }
        } else if (error.request) {
          // Network error or no response
          ToastService.error(
            'Không thể kết nối đến máy chủ. Vui lòng kiểm tra đường truyền.'
          )
        } else {
          ToastService.error(
            error.message || 'Có lỗi xảy ra. Vui lòng thử lại.'
          )
        }
      } finally {
        isProcessing.value.confirmBooking = false
      }
    }

    const legendItems = [
      {
        type: 'standard',
        label: 'Available',
        price: 0,
        priceString: 'Standard Price'
      },
      {
        type: 'unavailable',
        label: 'Not Available',
        price: 0,
        priceString: '---'
      }
    ]

    const holdTicketInfo = ref(null)
    const remainingTime = ref('')
    const holdTimer = ref(null)

    const startHoldTimer = endTime => {
      if (holdTimer.value) {
        clearInterval(holdTimer.value)
      }

      holdTimer.value = setInterval(() => {
        const now = new Date().getTime()
        const end = new Date(endTime).getTime()
        const timeLeft = end - now

        if (timeLeft <= 0) {
          clearInterval(holdTimer.value)
          holdTicketInfo.value = null
          remainingTime.value = ''
          secureLocalStorage.removeItem('holdTicket')
          return
        }

        const minutes = Math.floor(timeLeft / 60000)
        const seconds = Math.floor((timeLeft % 60000) / 1000)
        remainingTime.value = `${minutes}:${seconds
          .toString()
          .padStart(2, '0')}`
      }, 1000)
    }

    // Add helper function for checking hold status and starting timer
    const checkAndStartHoldTimer = async holdData => {
      try {
        // Format slot numbers as comma-separated string
        const slotNumbersStr = holdData.slot_numbers.join(',')

        const response = await axiosInstance.get(
          `${API_BASE_URL}/hold_slots/status`,
          {
            params: {
              trip_id: holdData.trip_id,
              slot_numbers: slotNumbersStr // Send as single string
            },
            paramsSerializer: params => {
              // Add custom serializer
              return Object.entries(params)
                .map(([key, value]) => `${key}=${value}`)
                .join('&')
            }
          }
        )

        if (response.data.valid) {
          startHoldTimer(response.data.hold_until)
        } else {
          clearInterval(holdTimer.value)
          holdTicketInfo.value = null
          secureLocalStorage.removeItem('holdTicket')
          selectedSeats.value = []
          ToastService.error('Phiên giữ ghế đã hết hạn. Vui lòng chọn lại ghế.')
          return false
        }
        return true
      } catch (error) {
        console.error('Error checking hold status:', error)
        return false
      }
    }

    const fetchTrips = async (routeId, orderBy = 'asc') => {
      try {
        isProcessing.value.fetchingTrips = true

        // Make initial trip request
        const tripPromise = axiosInstance.get(`${API_BASE_URL}/trips/`, {
          params: {
            route_id: routeId,
            status: 'scheduled',
            order_by: orderBy,
            per_page: 1000
          }
        })

        // Show loading state immediately
        trips.value = [] // Clear old trips to show loading state

        // Wait for trip data
        const response = await tripPromise
        trips.value = response.data

        // Now fetch availability in parallel if we have trips
        if (trips.value.length > 0) {
          const tripIds = trips.value.map(trip => trip.trip_id).join(',')
          isRefreshingSeats.value = true

          // Don't await this - let it update in background
          axiosInstance
            .get(`${API_BASE_URL}/trips/availability`, {
              params: { trip_ids: tripIds }
            })
            .then(availabilityResponse => {
              if (Array.isArray(availabilityResponse.data)) {
                availabilityResponse.data.forEach(tripData => {
                  const tripIndex = trips.value.findIndex(
                    t => t.trip_id === tripData.trip_id
                  )
                  if (tripIndex >= 0) {
                    trips.value[tripIndex].available_seats =
                      tripData.available_seats
                  }
                })
              }
              isRefreshingSeats.value = false
            })
            .catch(error => {
              console.error('Error fetching seat availability:', error)
              isRefreshingSeats.value = false
            })
        }

        return response
      } catch (error) {
        console.error('Error fetching trips:', error)
        isRefreshingSeats.value = false
        return null
      } finally {
        isProcessing.value.fetchingTrips = false
      }
    }

    const cancelHoldTicket = async (reload = false) => {
      if (isProcessing.value.cancelHoldTicket) return

      try {
        isProcessing.value.cancelHoldTicket = true

        if (!holdTicketInfo.value) return

        // Release all held seats
        const releasePromises = holdTicketInfo.value.slot_numbers.map(seatId =>
          axiosInstance.delete(`${API_BASE_URL}/hold_slots/`, {
            data: {
              trip_id: holdTicketInfo.value.trip_id,
              slot_number: seatId
            }
          })
        )

        await Promise.all(releasePromises)

        // Clear hold information
        holdTicketInfo.value = null
        secureLocalStorage.removeItem('holdTicket')
        if (holdTimer.value) {
          clearInterval(holdTimer.value)
        }

        // Clear selections
        selectedSeats.value = []

        // Reload page to reset state
        //Show confirmation message
        ToastService.success('Đã hủy giữ vé thành công. Vui lòng chọn lại ghế.')
        if (reload) {
          location.reload()
        }
      } catch (error) {
        console.error('Error canceling hold ticket:', error)
      } finally {
        setTimeout(() => {
          isProcessing.value.cancelHoldTicket = false
        }, 1000)
      }
    }

    watch(selectedTicketType, async (newTicketTypeId, oldTicketTypeId) => {
      // Skip if same ticket type or first selection
      if (newTicketTypeId === oldTicketTypeId || !oldTicketTypeId) {
        return
      }

      // Get ticket types info for logging
      const newTicket = ticketTypes.value.find(
        t => t.ticket_type_id === newTicketTypeId
      )
      const oldTicket = ticketTypes.value.find(
        t => t.ticket_type_id === oldTicketTypeId
      )


      // Check if we need to handle special ticket type transitions
      const wasSpecial =
        oldTicket && oldTicket.independent && oldTicket.independent === true
      const isNowSpecial =
        newTicket && newTicket.independent && newTicket.independent === true

      // If we're switching between regular and special tickets or vice versa, handle seat releases
      if (
        wasSpecial !== isNowSpecial ||
        (!wasSpecial && !isNowSpecial && holdTicketInfo.value)
      ) {
        // If there were previously held seats, release them
        if (
          holdTicketInfo.value &&
          holdTicketInfo.value.slot_numbers.length > 0
        ) {
          try {
            // Release all held seats
            const releasePromises = holdTicketInfo.value.slot_numbers.map(
              seatId =>
                axiosInstance.delete(`${API_BASE_URL}/hold_slots/`, {
                  data: {
                    trip_id: holdTicketInfo.value.trip_id,
                    slot_number: seatId
                  }
                })
            )

            await Promise.all(releasePromises)

            // Clear hold timer
            if (holdTimer.value) {
              clearInterval(holdTimer.value)
              holdTimer.value = null
            }

            // Clear hold info
            holdTicketInfo.value = null
            secureLocalStorage.removeItem('holdTicket')

            // Reset selected seats
            selectedSeats.value = []

            // Show notification to user
            ToastService.success(
              'Thay đổi loại vé thành công. Ghế đã giữ trước đó đã được hủy.'
            )
          } catch (error) {
            console.error(
              'Error releasing held seats on ticket type change:',
              error
            )
          }
        }
      } else if (holdTicketInfo.value) {
        // If we're not changing between special and regular but staying in the same category
        const newTicketType = ticketTypes.value.find(
          t => t.ticket_type_id === newTicketTypeId
        )
        if (newTicketType) {
          holdTicketInfo.value.ticketType = newTicketType
          secureLocalStorage.setItem('holdTicket', holdTicketInfo.value)
        }
      }
    })

    watch(selectedRoute, async newRouteId => {
      if (newRouteId) {
        // Clear previous locations immediately
        pickupLocations.value = []

        try {
          // Force a component update after fetching
          nextTick(() => {
            if (pickupLocations.value && pickupLocations.value.length > 0) {
              const locations = [...pickupLocations.value]
              pickupLocations.value = []
              setTimeout(() => {
                pickupLocations.value = locations
              }, 0)
            }
          })
        } catch (error) {
          console.error('Error in selectedRoute watcher:', error)
        }
      } else {
        pickupLocations.value = []
      }
    })

    watch(sortOption, async newSortOption => {
      if (selectedRoute.value) {
        await fetchTrips(selectedRoute.value, newSortOption)
      }
    })

    watch(
      selectedSeats,
      newSeats => {
        // If the number of members is less than selected seats, add more
        while (groupMembers.value.length < newSeats.length) {
          groupMembers.value.push({
            name: '',
            phone: '',
            email: '',
            birthYear: null,
            pickupLocationId: ''
          })
        }

        // If there are more members than seats, remove extras
        while (
          groupMembers.value.length > newSeats.length &&
          groupMembers.value.length > 1
        ) {
          groupMembers.value.pop()
        }

        // Always ensure active member index is valid
        if (activeMemberIndex.value >= groupMembers.value.length) {
          activeMemberIndex.value = Math.max(0, groupMembers.value.length - 1)
        }
      },
      { immediate: true }
    )

    const increaseTicketQuantity = () => {
      if (specialTicketQuantity.value < 10) {
        specialTicketQuantity.value++
      }
    }

    const decreaseTicketQuantity = () => {
      if (specialTicketQuantity.value > 1) {
        specialTicketQuantity.value--
      }
    }

    const validateQuantity = () => {
      // Ensure quantity is an integer between 1 and 10
      let value = parseInt(specialTicketQuantity.value)
      if (isNaN(value) || value < 1) {
        specialTicketQuantity.value = 1
      } else if (value > 10) {
        specialTicketQuantity.value = 10
      } else {
        specialTicketQuantity.value = value
      }
    }

    // Add this watch function to update group members based on special ticket quantity
    watch(specialTicketQuantity, newQuantity => {
      // For special tickets, sync group members count with ticket quantity
      if (isSpecialTicketType.value) {
        // Add more members if needed
        while (groupMembers.value.length < newQuantity) {
          groupMembers.value.push({
            name: '',
            phone: '',
            email: '',
            birthYear: null,
            pickupLocationId: '',
            gender: '',
            cccd: '',
            cccdAddress: '',
            birthDate: '',
            birthDay: '',
            birthMonth: ''
          })
        }

        // Remove excess members if quantity decreased
        while (groupMembers.value.length > newQuantity && groupMembers.value.length > 1) {
          groupMembers.value.pop()
        }

        // Ensure active member index is valid
        if (activeMemberIndex.value >= groupMembers.value.length) {
          activeMemberIndex.value = Math.max(0, groupMembers.value.length - 1)
        }
      }
    })

    // Watch for search query changes and update URL with debounce
    watch(routeSearchQuery, debounce((newQuery) => {
      const url = new URL(window.location)
      if (newQuery) {
        url.searchParams.set('search', newQuery)
      } else {
        url.searchParams.delete('search')
      }
      window.history.replaceState({}, '', url)
    }, 300))

    return {
        showGroupBookingModal,
        isHoldTicketCollapsed,
        isInitialLoading,
        isProcessing,
        seatRefreshInterval,
        isRefreshingSeats,
        refreshAvailableSeatCounts,
        phoneError,
        emailError,
        cccdError,
        displayedGroupMembers,
        isSpecialTicketType,
        groupMembers,
        currentMember,
        activeMemberIndex,
        addNewMember,
        removeMember,
        switchToMember,
        pickupLocations,
        DEBUG_MODE,
        heldSeats,
        routes,
        trips,
        to,
        bookings,
        selectedRoute,
        selectedTrip,
        selectedSeats,
        currentStep,
        steps,
        completedSteps, // Đưa completedSteps vào return
        canProceed,
        nextStep,
        previousStep,
        navigateToStep,
        currentRoute,
        filteredTrips,
        seatGrid,
        ticketTypes,
        selectedTicketType,
        handleTicketTypeSelect,
        getSelectedTicketTypeName,
        calculateTotalPrice,
        getBookedSeats,
        canBook,
        handleRouteSelect,
        handleSeatSelect,
        handleTripSelect,
        addonServicesRef,
        isBooked,
        getAvailableSeats,
        formatDateTime,
        getValidationMessage,
        legendItems,
        holdTicketInfo,
        remainingTime,
        checkAndStartHoldTimer,
        monthFilters,
        selectedMonthFilter,
        handleMonthFilter,
        currentMonth,
        currentYear,
        routeSearchQuery,
        filteredRoutes,
        formatCurrency,
        specialTicketQuantity,
        increaseTicketQuantity,
        decreaseTicketQuantity,
        validateQuantity,
        sortOption,
        sortedTrips,
        visibleTicketTypes,
        selectedAddons,
        totalWithAddons,
        getAddonName,
        getAddonPrice,
        addonProducts,
        fetchAddonProducts,
        calculateAddonTotal,
        calculateTotalWithAddons,
        isSearching,
        confirmBooking,
        cancelHoldTicket,
        confirmHoldTicket,
        onCccdInput,
        parseCCCD
      }
  }
}
</script>

<style lang="scss" scoped>
/* ========================================
   CRITICAL FIX: Box-sizing and Overflow
   ======================================== */
   
/* Apply box-sizing to all elements to prevent padding from causing overflow */
:deep(*),
:deep(*::before),
:deep(*::after),
*,
*::before,
*::after {
  box-sizing: border-box;
}

/* Prevent horizontal overflow on main containers */
.seat-booking,
.seat-booking__wrapper,
.step-content,
.step-page,
.step-card {
  overflow-x: hidden;
  max-width: 100%;
}

/* Add new styles for the initial loading container */
.initial-loading-container {
  min-height: 400px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin: 2rem 0;
  border-radius: 1rem;
  background: white;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

/* Updated Filter Section Styles */
.filter-section {
  background: rgba(255, 255, 255, 0.98);
  border-radius: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  padding: 1.25rem;
  margin-bottom: 1.5rem;
}

.filter-header {
  display: flex;
  flex-direction: column;
  margin-bottom: 1.5rem;
  position: relative;
  gap: 0.5rem;
}

/* Enhanced Month Filter Styles */
.month-filter {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%; /* Take full width */
  position: relative; /* Remove absolute positioning */
  z-index: 1;

  .month-filter-btn {
    padding: 0.625rem 1.25rem;
    border: 2px solid #e2e8f0;
    border-radius: 0.75rem;
    background: white;
    color: #075985;
    font-weight: 500;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(.active) {
      border-color: rgba(22, 163, 74, 0.6);
      color: #16a34a;
      transform: translateY(-1px);
      box-shadow: 0 4px 10px rgba(22, 163, 74, 0.15);
    }

    &.active {
      background: linear-gradient(135deg, #16a34a, #15803d);
      color: white;
      border-color: transparent;
      box-shadow: 0 8px 16px rgba(22, 163, 74, 0.25);
    }
  }
}

/* Improved Search Box Styles */
.search-box {
  min-width: 200px;
  /* flex-shrink: 0; */
  /* align-self: center; */
  position: relative; /* Add this to contain the absolute positioned icon */
  margin: 0 auto; /* Add auto margins to center */
  display: flex; /* Add flex display */
  justify-content: right; /* Center horizontally */
  width: 100%; /* Take full width to allow centering */

  input {
    width: 100%; /* Use full width */
    max-width: 350px; /* Limit width */
    padding: 0.75rem 1rem 0.75rem 2.5rem; /* Add padding for icon */
    border: 2px solid #e2e8f0;
    border-radius: 0.75rem;
    font-size: 0.875rem;
    color: #064e3b;
    transition: all 0.2s ease;

    &::placeholder {
      color: #a0aec0;
    }

    &:focus {
      outline: none;
      border-color: #0f766e;
      box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.18);
    }
  }

  i {
    position: absolute;
    left: calc(50% - 160px); /* Position icon relative to center */
    top: 50%;
    transform: translateY(-50%);
    color: #a0aec0;
    font-size: 1rem;
    pointer-events: none;
    transition: color 0.2s ease;
  }

  &:focus-within i {
    color: #16a34a;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .seat-booking {
    padding: 0.25rem 0;
  }

  .seat-booking__wrapper {
    padding: 0.5rem 0.25rem;
    border-radius: 0.5rem;
  }

  .step-card {
    padding: 0.75rem 0.5rem;
    border-radius: 0.5rem;
  }

  .step-navigation {
    .step-circle {
      width: 2rem;
      height: 2rem;
    }

    .step-label {
      font-size: 0.75rem;
      max-width: 75px;
    }
  }

  .location-buttons,
  .time-slots,
  .ticket-types {
    grid-template-columns: 1fr;
  }

  .hold-ticket-info {
    flex-direction: column;
    padding: 1rem;
    gap: 1rem;

    .selected-info,
    .action-container {
      width: 100%;
    }

    .action-container {
      display: grid;
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }
  }

  .booking-summary {
    flex-direction: column;
    gap: 1rem;
    text-align: center;

    .selected-info,
    .total-price {
      width: 100%;
      text-align: center;
    }
  }

  .nav-btn {
    padding: 0.625rem 1.25rem;
    font-size: 0.875rem;
  }
  .filter-header {
    flex-direction: column;
    gap: 1rem;
  }

  .month-filter {
    position: static;
    transform: none;
    order: 1;
    width: 100%;
  }

  .search-box {
    order: 2;
    width: 100%; /* Full width on mobile */
    margin: 0 auto;

    input {
      max-width: 90%; /* Wider on mobile */
    }

    i {
      left: 8%; /* Adjust icon position on mobile */
    }
  }
}

/* Animation for Filter Changes */
@keyframes filterFade {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.filter-section {
  animation: filterFade 0.3s ease-out;
}

/* General Styles */
.seat-booking {
  position: relative;
  padding: 2rem 1.5rem;
  min-height: 100vh;
  background-image: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(212, 238, 219, 0.9) 100%),
    url('https://images.unsplash.com/photo-1506905925346-21bda4d32df4?q=80&w=2000');
  background-position: center;
  background-size: cover;
  background-attachment: fixed;
  overflow: hidden;
}

.seat-booking::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(at 20% 30%, rgba(34, 139, 230, 0.08) 0%, transparent 50%),
    radial-gradient(at 80% 20%, rgba(16, 185, 129, 0.06) 0%, transparent 50%);
  opacity: 0.7;
  pointer-events: none;
}

.seat-booking::after {
  content: '';
  position: absolute;
  bottom: -100px;
  right: -100px;
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(14, 165, 233, 0.12) 0%, transparent 70%);
  pointer-events: none;
  transform: rotate(-15deg);
}

.seat-booking__wrapper {
  position: relative;
  z-index: 1;
  max-width: 1180px;
  margin: 0 auto;
  padding: 2rem 2.5rem;
  border-radius: 1.5rem;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  overflow: hidden;

}

.steps-wrapper {
  position: relative;
  width: 100%;
  max-width: 1000px;
  margin: 0 auto 0.5rem auto;
  display: flex;
  justify-content: center;
  padding: 0.5rem 0;
}

.step-navigation {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  padding: 0 1.5rem;

  .step {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    flex: 1;
    cursor: pointer;
    min-height: 80px;
    padding: 0 8px;
    transition: transform 0.2s ease;

    &:hover:not(.completed):not(.active) .step-circle {
      transform: translateY(-2px);
      box-shadow: 0 6px 12px rgba(14, 165, 233, 0.22);
    }

    &.active .step-circle {
      background: linear-gradient(135deg, #0ea5e9, #16a34a);
      border-color: transparent;
      color: white;
      transform: scale(1.07);
      box-shadow: 0 0 18px rgba(14, 165, 233, 0.28);
    }

    &.completed .step-circle {
      background: linear-gradient(135deg, #16a34a, #059669);
      border-color: transparent;
      color: white;
    }
  }

  .step-circle {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    background: white;
    border: 2px solid rgba(226, 232, 240, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    margin-bottom: 0.5rem;
    transition: all 0.3s;
    position: relative;
    z-index: 2;
    flex-shrink: 0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
  }

  .step-connector {
    position: absolute;
    top: 20px;
    left: calc(50% + 1.25rem);
    height: 2px;
    width: calc(100% - 2.5rem);
    z-index: 1;
    transition: background-color 0.5s ease;
    background: rgba(209, 250, 229, 0.6);
  }
}

/* Add media queries for responsive design */
@media (max-width: 768px) {
  .steps-wrapper {
    padding: 0 0.5rem;
  }

  .step-navigation {
    padding: 0;

    .step {
      padding: 0 4px;
    }

    .step-circle {
      width: 2rem;
      height: 2rem;
      font-size: 0.875rem;
    }

    .step-label {
      font-size: 0.75rem;
      max-width: 80px;
    }
  }
}

@media (max-width: 480px) {
  .seat-booking__wrapper {
    padding: 1.5rem 1rem;
  }

  .step-card {
    padding: 1.25rem 1rem;
  }

  .step-navigation {
    .step-circle {
      width: 1.75rem;
      height: 1.75rem;
      font-size: 0.75rem;
    }

    .step-label {
      font-size: 0.7rem;
      max-width: 60px;
    }
  }

  /* Fix ticket cards alignment on mobile */
  .ticket-type-select {
    margin-bottom: 0.75rem;
  }

  .ticket-types {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
  }

  .ticket-type-btn {
    width: 100%;
    min-width: unset;
    max-width: 300px;
    padding: 0.75rem;
    margin: 0 auto;
  }

  .ticket-type-info {
    text-align: left;
    width: 100%;

    .ticket-name {
      font-size: 1rem;
      margin-bottom: 0.25rem;
    }

    .ticket-description {
      font-size: 0.75rem;
      margin-bottom: 0.35rem;
      line-height: 1.3;
    }

    .ticket-price {
      font-size: 1.1rem;
      margin-bottom: 0;
    }
  }
}
.step-label {
  font-size: 0.875rem;
  color: #075985;
  font-weight: 500;
  text-align: center; /* Căn giữa chữ */
}

.step-content {
  position: relative;
  //min-height: 400px; /* Giữ chiều cao tối thiểu, tránh bị co lại */
}

.step-page {
  animation: fadeIn 0.3s ease-in-out;
}

.step-card {
  background: rgba(255, 255, 255, 0.98);
  border-radius: 1rem;
  padding: 1.75rem 2rem;
  border: 1px solid rgba(226, 232, 240, 0.6);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.03);
  transition: transform 0.25s ease, box-shadow 0.3s ease;
}

.step-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06), 0 2px 4px rgba(0, 0, 0, 0.04);
}
.booking-header {
  margin-bottom: 2rem;
}

.ticket-type-select {
  margin-bottom: 2rem;
}

.ticket-types {
  display: grid;
  /* Use min() to prevent overflow on small screens */
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 300px), 1fr));
  gap: 1.5rem;
}

.ticket-type-info {
  .ticket-name {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #064e3b;
  }

  .ticket-description {
    color: #718096;
    font-size: 0.875rem;
    margin-bottom: 1rem;
  }

  .ticket-price {
    font-size: 1.5rem;
    font-weight: 600;
    color: #16a34a;
    margin-bottom: 1rem;
  }

  .ticket-features {
    .feature {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #075985;
      font-size: 0.875rem;
      margin-bottom: 0.5rem;

      i {
        color: #16a34a;
      }
    }
  }
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
  margin-top: auto;
}

.location-select,
.time-select {
  margin-bottom: 2rem;
}

.location-buttons,
.time-slots {
  display: grid;
  /* Use min() to prevent overflow on small screens */
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 250px), 1fr));
  gap: 1rem;
}

.location-btn,
.time-btn {
  background: white;
  border: 1px solid rgba(148, 163, 184, 0.15);
  padding: 1.25rem;
  border-radius: 1rem;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.03);
  font-family: inherit;

  &.active {
    border-color: rgba(22, 163, 74, 0.4);
    background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
    box-shadow: 0 4px 12px rgba(22, 163, 74, 0.12);
    transform: translateY(-2px);
  }

  &:hover:not(.active) {
    border-color: rgba(22, 163, 74, 0.25);
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(15, 23, 42, 0.06);
  }
}

.time-btn.is-loading {
  position: relative;
  overflow: hidden;
}

.time-btn.is-loading::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  width: 100%;
  background: linear-gradient(90deg, transparent, #0f766e, transparent);
  animation: loadingBar 1.5s infinite;
}

@keyframes loadingBar {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.ticket-type-btn {
  background: white;
  border: 1px solid #e2e8f0;
  padding: 1rem;
  border-radius: 1rem;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.03);

  &.active {
    border-color: rgba(22, 163, 74, 0.6);
    background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
    box-shadow: 0 10px 20px rgba(22, 163, 74, 0.2);
    transform: translateY(-3px);
  }

  &:hover:not(.active) {
    border-color: rgba(22, 163, 74, 0.45);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
  }

  .ticket-type-info {
    .ticket-name {
      font-size: 1.3rem;
      font-weight: 600;
      margin-bottom: 0.75rem;
      color: #064e3b;
    }

    .ticket-price {
      font-size: 1.6rem;
      font-weight: 600;
    color: #16a34a;
      margin-bottom: 1rem;
    }
  }
}

.route-info {
  .cities {
    font-size: 1.125rem;
    font-weight: 500;
    margin-bottom: 0.5rem;


    .arrow {
      color: #718096;
      margin: 0 0.5rem;

    }
  }

  .price {
    color: #16a34a;
    font-weight: 500;

  }
}

.time-btn {
  text-align: center;

  .name {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #064e3b;
  }

  .time {

    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 0.25rem;
  }

  .driver-info {
    color: #075985;
    font-size: 0.85rem;
    margin: 0.5rem 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;

    i {
      color: #16a34a;
    }

    .license-plate {
      background: #f0fdf4;
      padding: 2px 6px;
      border-radius: 4px;
      margin-left: 4px;
      font-size: 0.8rem;
      color: #064e3b;
      font-weight: 500;
    }
  }

  .seats-left {
    color: #718096;
    font-size: 0.7rem;
    margin-bottom: 0.25rem;

  }

  .price {
    color: #16a34a;
    font-weight: 500;

  }
}

.seat-legend {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 2rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.legend-color {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 0.375rem;

  &--standard {
    background: #0f766e;
  }

  &--selected {
    background: #0ea5e9;
  }

  &--unavailable {
    background: #e2e8f0;
  }
}

.legend-label {
  color: #075985;
  font-size: 0.875rem;
}

.legend-price {
  color: #16a34a;
  font-size: 0.875rem;
  margin-left: auto;
}

.booking-summary {
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
  .selected-info {
    .ticket-type {
      color: #718096;
      font-size: 0.875rem;
      margin: 0.25rem 0;
    }
  }
}

.booking-summary {
  margin-top: 2rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.95) 0%, rgba(239, 246, 255, 0.9) 50%, rgba(236, 253, 245, 0.92) 100%);
  border-radius: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(226, 232, 240, 0.6);


  .selected-info {
    text-align: left;

    .route {
      font-size: 1.25rem;
      font-weight: 600;
      margin-bottom: 0.5rem;
      color: #1a202c;
    }

    .time,
    .seats,
    .ticket-type {
      color: #075985;
      font-size: 0.925rem;
      margin-bottom: 0.375rem;
    }
  }

  .total-price {
    text-align: right;

    .amount {
      display: block;
      font-size: 1.625rem;
      font-weight: 600;
      color: #16a34a;
      margin-bottom: 0.5rem;
      text-shadow: 0 1px 2px rgba(22, 163, 74, 0.12);
    }
  }
}

.selected-info {
  text-align: left;
  .hold-ticket {
    color: #718096;
    font-size: 0.875rem;
    margin-bottom: 0.25rem;
  }
  .route {
    font-size: 1.125rem;
    font-weight: 600;
    margin-bottom: 0.25rem;
  }

  .time,
  .seats {
    color: #718096;
    font-size: 0.875rem;
  }
}

.total-price {
  text-align: right;

  .amount {
    display: block;
    font-size: 1.5rem;
    font-weight: 600;
    color: #0ea5e9;
    margin-bottom: 0.5rem;
  }
}

.confirm-btn {
  background: linear-gradient(135deg, #0ea5e9, #16a34a);
  color: white;
  padding: 0.75rem;
  border-radius: 0.5rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: linear-gradient(135deg, #0ea5e9, #047857);
  }
}
.step-navigation-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
  margin-bottom: auto;
  padding-bottom: 1.5rem;

  .btn-container {
    &.left {
      // Styles for left button container, if needed
    }
    &.right {
      // Styles for right button container, if needed
    }
  }
}
.nav-btn {
  padding: 0.75rem 1.5rem;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  &.back {
    background-color: #edf2f7;
    color: #064e3b;
    &:hover {
      background-color: #cbd5e0;
    }
  }

  &.next {
    background: linear-gradient(135deg, #16a34a, #15803d);
    color: white;
    box-shadow: 0 10px 20px rgba(22, 163, 74, 0.25);
    &:hover {
      background: linear-gradient(135deg, #15803d, #14532d);
      transform: translateY(-1px);
      box-shadow: 0 14px 24px rgba(22, 163, 74, 0.28);
    }
    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
      background: linear-gradient(135deg, #a7f3d0, #bbf7d0);
      box-shadow: none;
    }
  }
}
/* Updated Step Connector Styles */
.step-connector {
  position: absolute;
  top: 20px; /* Adjust as needed */
  left: calc(
    50% + 1.25rem
  ); /* Bắt đầu từ mép phải của step-circle hiện tại (1.25rem là nửa chiều rộng của step-circle */
  height: 2px;
  width: calc(100% - 2.5rem); /*  chiều rộng của step-circle */
  z-index: 1;
}

span {

}

button {

}

.customer-info {
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem;

  .customer-form {
    margin-top: 2rem;

    .form-group {
      margin-bottom: 1.75rem;

      label {
        display: block;
        margin-bottom: 0.625rem;
        font-weight: 500;
        color: #064e3b;
        font-size: 0.95rem;
        transition: color 0.2s;
      }

      input,
      select {
        width: 100%;
        padding: 0.85rem;
        border: 1.5px solid #e2e8f0;
        border-radius: 0.5rem;
        font-size: 1rem;
        transition: all 0.2s ease;
        background-color: #f9fafc;

        &:focus {
          outline: none;
          border-color: #15803d;
          background-color: #fff;
          box-shadow: 0 0 0 4px rgba(22, 163, 74, 0.15);
        }

        &::placeholder {
          color: #a0aec0;
          opacity: 0.7;
        }
      }

      &:focus-within label {
        color: #15803d;
      }
    }



    /* DOB split row for leader */
    .dob-split-row {
      display: flex;
      gap: 1rem;
      align-items: center;
    }

    .dob-daymonth {
      display: flex;
      align-items: center;
      gap: 0.4rem;
      flex: 1;
    }

    .dob-separator {
      font-size: 1.2rem;
      font-weight: 600;
      color: #718096;
    }

    .dob-input {
      width: 100%;
      padding: 0.85rem;
      border: 1.5px solid #e2e8f0;
      border-radius: 0.5rem;
      font-size: 1rem;
      text-align: center;
      background-color: #f9fafc;
      transition: all 0.2s ease;

      &:focus {
        outline: none;
        border-color: #15803d;
        background-color: #fff;
        box-shadow: 0 0 0 4px rgba(22, 163, 74, 0.15);
      }

      &::placeholder {
        color: #a0aec0;
        opacity: 0.7;
      }
    }

    .dob-year {
      flex: 0.8;
    }

    .dob-year-input {
      font-weight: 600;
      color: #064e3b;
    }



    /* Full date input for members */
    .date-input {
      width: 100%;
      padding: 0.85rem;
      border: 1.5px solid #e2e8f0;
      border-radius: 0.5rem;
      font-size: 1rem;
      background-color: #f9fafc;
      transition: all 0.2s ease;

      &:focus {
        outline: none;
        border-color: #15803d;
        background-color: #fff;
        box-shadow: 0 0 0 4px rgba(22, 163, 74, 0.15);
      }
    }

    .label-highlight {
      color: #c53030 !important;
      font-weight: 600 !important;
    }

    .confirm-booking-btn {
      width: 100%;
      padding: 1.125rem;
      background: linear-gradient(to right, #16a34a, #15803d);
      color: white;
      border: none;
      border-radius: 0.5rem;
      font-weight: 600;
      font-size: 1.125rem;
      cursor: pointer;
      transition: all 0.3s;
      margin-top: 2rem;
      box-shadow: 0 4px 6px rgba(56, 161, 105, 0.3);

      &:hover:not(:disabled) {
        background: linear-gradient(to right, #15803d, #047857);
        transform: translateY(-2px);
        box-shadow: 0 6px 12px rgba(56, 161, 105, 0.4);
      }

      &:disabled {
        background: linear-gradient(to right, #a0aec0, #cbd5e0);
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
      }

      &:active:not(:disabled) {
        transform: translateY(1px);
        box-shadow: 0 2px 4px rgba(56, 161, 105, 0.4);
      }
    }
  }
}

.hold-ticket-info {
  position: sticky;
  top: 0;
  z-index: 100;
  margin: -1rem -1rem 1.5rem -1rem;
  padding: 1rem 1.75rem;
  justify-content: space-between;
  align-items: center;
  border-radius: 0.75rem;
  border-bottom: 1px solid #e2e8f0;
  transition: box-shadow 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border-radius: 0.75rem;
  background: linear-gradient(to right, rgba(255, 255, 255, 0.98), rgba(249, 250, 252, 0.95));
  margin-bottom: 1.5rem;
  padding: 0.75rem 1.5rem;

  &.collapsed {
    .hold-ticket-details {
      display: none;
    }
  }

  .hold-ticket-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;

    .hold-ticket {
      font-weight: 600;
      color: #0f766e;
      margin-right: 1rem;
    }
    .route {
      color: #064e3b;
      margin-right: 1rem;
    }
    .hold-until {
      color: #e53e3e;
      font-weight: 500;
      background: #fff5f5;
      border-radius: 0.25rem;
      padding: 0.2rem 0.5rem;
      font-size: 0.95rem;
    }
    .toggle-btn {
      background: none;
      border: none;
      font-size: 1.25rem;
      cursor: pointer;
      color: #16a34a;
      padding: 0.25rem 0.5rem;
      border-radius: 0.5rem;
      transition: background 0.2s;
      &:hover {
        background: #f0fdf4;
      }
    }
  }

  .hold-ticket-details {
    margin-top: 0.5rem;
    animation: fadeIn 0.2s;
  }

  .hold-until {
    color: #e53e3e;
    font-weight: 600;
    font-size: 0.875rem;
    background: rgba(229, 62, 62, 0.1);
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    display: inline-block;
    margin-top: 0.25rem;
  }

  .action-container {
    display: flex;
    gap: 1rem;
    align-items: center;
    margin-top: 0.5rem;
  }

  .confirm-hold-btn {
    background: linear-gradient(to right, #16a34a, #15803d);
    color: white;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 0.5rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 2px 4px rgba(56, 161, 105, 0.2);

    &:hover {
      background: linear-gradient(to right, #15803d, #047857);
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(56, 161, 105, 0.3);
    }
  }

  .cancel-hold-btn {
    background: linear-gradient(to right, #e53e3e, #c53030);
    color: white;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 0.5rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 2px 4px rgba(197, 48, 48, 0.2);

    &:hover {
      background: linear-gradient(to right, #c53030, #9b2c2c);
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(197, 48, 48, 0.3);
    }
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.form-group {
  &:has(select),
  &:has([type='select']) {
    position: relative;
    margin-bottom: 2rem;

    label {
      display: block;
      margin-bottom: 0.75rem;
      font-weight: 500;
      color: #064e3b;
      font-size: 0.95rem;
      transition: color 0.2s;
    }

    select {
      width: 100%;
      padding: 1.1rem 3rem 1.1rem 1rem;
      appearance: none;
      -webkit-appearance: none;
      -moz-appearance: none;
      background-color: #f9fafc;
      border: 1.5px solid #e2e8f0;
      border-radius: 0.5rem;
      font-size: 1rem;
      color: #064e3b;
      cursor: pointer;
      transition: all 0.25s ease;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23264039' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 0.85rem center;
      background-size: 1.25rem;

      &:focus {
        outline: none;
        border-color: #0f766e;
        background-color: #fff;
        box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.18);
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%233182ce' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='18 15 12 9 6 15'%3E%3C/polyline%3E%3C/svg%3E");
      }

      &:hover:not(:disabled) {
        border-color: #0f766e;
        background-color: #f7fafc;
        transform: translateY(-1px);
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.07);
      }

      option {
        padding: 12px 15px;
        background-color: #fff;
        color: #064e3b;
        font-size: 0.95rem;

        &:hover {
          background-color: #f0fdf4;
        }
      }
    }

    /* Area that appears when dropdown is open */
    &::before {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 5%;
      width: 90%;
      height: 0;
      background: white;
      box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
      border-radius: 0 0 0.5rem 0.5rem;
      opacity: 0;
      z-index: -1;
      transition: height 0.3s, opacity 0.3s;
    }

    select:focus + &::before {
      height: 100px;
      opacity: 1;
    }

    /* Dropdown appearance when it's active or focused */
    select:focus,
    select:active {
      border-radius: 0.5rem 0.5rem 0 0;
    }

    /* Add a focus indicator line */
    .select-focus-indicator {
      position: absolute;
      bottom: 0;
      left: 0;
      height: 2px;
      width: 0;
      background: linear-gradient(to right, #0f766e, #63b3ed);
      transition: width 0.3s ease;
      opacity: 0;
    }

    select:focus + .select-focus-indicator {
      width: 100%;
      opacity: 1;
    }
  }

  /* Enhanced styling for the pickup location specifically */
  #pickupLocation {
    font-weight: 500;
    position: relative;
    z-index: 5;

    option {
      font-weight: normal;
      padding: 0.75rem 1rem;
      transition: background-color 0.2s;

      &:first-child {
        font-style: italic;
        color: #718096;
      }

      &:checked {
        background-color: #ebf8ff;
        color: #075985;
        font-weight: 500;
      }
    }
  }
}

/* Style the placeholder option differently */
select option[value=''][disabled] {
  color: #a0aec0;
  font-style: italic;
}

/* Custom style for the dropdown when locations are loading */
.form-group select:disabled {
  background-color: #f1f5f9;
  color: #94a3b8;
  border-color: #e2e8f0;
  cursor: wait;
  background-image: none;
  position: relative;
}

/* Add shimmer effect when loading */
.form-group.loading {
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 75%;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      #15803d 50%,
      transparent 100%
    );
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
    opacity: 1;
    transition: opacity 0.3s;
  }

  select {
    background-image: none;

    &::after {
      content: '...';
      position: absolute;
      right: 1rem;
      animation: dots 1.5s infinite;
    }
  }
}

@keyframes dots {
  0%,
  20% {
    content: '.';
  }
  40%,
  60% {
    content: '..';
  }
  80%,
  100% {
    content: '...';
  }
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: 0 0;
  }
}

/* Custom dropdown list wrapper */
.dropdown-list-wrapper {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background: white;
  border-radius: 0 0 0.5rem 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  transition: max-height 0.3s, opacity 0.3s;
}

.form-group select:focus + .dropdown-list-wrapper {
  max-height: 300px;
  opacity: 1;
}

/* Enhanced mobile responsiveness */
@media (max-width: 768px) {
  .form-group {
    &:has(select) {
      select {
        padding: 0.95rem 2.5rem 0.95rem 0.95rem;
        font-size: 0.95rem;
        background-position: right 0.75rem center;
        background-size: 1.1rem;
      }
    }
  }
}

@media (max-width: 480px) {
  .form-group {
    &:has(select) {
      margin-bottom: 1.75rem;

      label {
        font-size: 0.9rem;
        margin-bottom: 0.5rem;
      }

      select {
        padding: 0.9rem 2rem 0.9rem 0.9rem;
        font-size: 0.9rem;
        background-size: 1rem;
        background-position: right 0.6rem center;
      }
    }

    /* Make loading indicator more visible on small screens */
    &.loading::after {
      height: 3px;
    }
  }
}

/* Support for older browsers that don't support :has() */
.form-group-select {
  position: relative;

  /* Same styling as the :has(select) above */
  label {
    display: block;
    margin-bottom: 0.75rem;
    font-weight: 500;
    color: #064e3b;
    font-size: 0.95rem;
    transition: color 0.2s;
  }

  select {
    width: 100%;
    padding: 1.1rem 3rem 1.1rem 1rem;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-color: #f9fafc;
    border: 1.5px solid #e2e8f0;
    border-radius: 0.5rem;
    font-size: 1rem;
    color: #064e3b;
    cursor: pointer;
    transition: all 0.25s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2315803d' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.85rem center;
    background-size: 1.25rem;

    &:focus {
      outline: none;
      border-color: #15803d;
      background-color: #fff;
      box-shadow: 0 0 0 4px rgba(22, 163, 74, 0.15);
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2315803d' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='18 15 12 9 6 15'%3E%3C/polyline%3E%3C/svg%3E");
    }
  }
}

.person-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 0.75rem;
}

.person-tab {
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 0.625rem 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  transition: all 0.2s ease;
  position: relative;

  .tab-badge {
    font-size: 0.6rem;
    font-weight: 600;
    padding: 0.15rem 0.5rem;
    border-radius: 0.25rem;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }

  .leader-badge {
    background: #c6f6d5;
    color: #22543d;
  }

  .member-badge {
    background: #e2e8f0;
    color: #075985;
  }

  &.active {
    background: #ecfdf5;
    border-color: #15803d;
    color: #14532d;
    font-weight: 500;
    box-shadow: 0 2px 4px rgba(22, 163, 74, 0.1);
  }

  &:hover:not(.active) {
    background: #edf2f7;
    transform: translateY(-1px);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }

  .remove-tab-btn {
    background: transparent;
    border: none;
    color: #a0aec0;
    font-size: 0.75rem;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 0.25rem;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: #e53e3e;
      color: white;
    }
  }
}

.add-tab-btn {
  background: #edf2f7;
  border: 1px dashed #cbd5e0;
  border-radius: 0.5rem;
  padding: 0.625rem 1rem;
  color: #075985;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  transition: all 0.2s;

  &:hover {
    background: #e2e8f0;
    border-color: #a0aec0;
    color: #064e3b;
  }

  i {
    font-size: 0.75rem;
  }
}

.group-info {
  color: #075985;
  font-size: 0.925rem;
  margin-bottom: 0.375rem;
  font-weight: 500;
}

/* Responsive styles for person tabs */
@media (max-width: 768px) {
  .person-tabs {
    flex-wrap: wrap;
    padding-bottom: 0.5rem;
  }

  .person-tab,
  .add-tab-btn {
    font-size: 0.8rem;
    padding: 0.5rem 0.75rem;
  }
}
.special-ticket-notification {
  display: flex;
  background: linear-gradient(to right, #ecfdf5, #d1fae5);
  border: 1px solid #6ee7b7;
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 5px rgba(22, 163, 74, 0.1);
}

.notification-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  background: #15803d;
  color: white;
  border-radius: 50%;
  margin-right: 1.25rem;
  font-size: 1.25rem;
  box-shadow: 0 3px 6px rgba(22, 163, 74, 0.2);
}

.notification-content {
  flex: 1;
}

.notification-content h4 {
  color: #14532d;
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 0.5rem;
}

.notification-content p {
  color: #075985;
  font-size: 0.925rem;
  margin: 0;
  line-height: 1.5;
}

/* Responsive styles */
@media (max-width: 640px) {
  .special-ticket-notification {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .notification-icon {
    margin-right: 0;
    margin-bottom: 1rem;
  }
}

.special-ticket-quantity {
  margin-top: 1.5rem;
  background-color: #f7fafc;
  padding: 1.5rem;
  border-radius: 0.75rem;
  border: 1px solid #e2e8f0;
}

.quantity-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.quantity-container h4 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #064e3b;
  margin: 0;
}

.quantity-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.quantity-btn {
  width: 3rem;
  height: 3rem;
  border-radius: 50%; /* Make buttons circular */
  border: 1.5px solid #e2e8f0;
  background: linear-gradient(to bottom, #ffffff, #f7fafc);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s ease;
  position: relative;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.quantity-btn i {
  font-size: 1.25rem;
  color: #075985;
  transition: all 0.2s;
}

.quantity-btn:hover:not(:disabled) {
  background: linear-gradient(to bottom, #15803d, #14532d);
  border-color: #15803d;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(22, 163, 74, 0.3);
}

.quantity-btn:hover:not(:disabled) i {
  color: white;
  transform: scale(1.2);
}

.quantity-btn:active:not(:disabled) {
  transform: translateY(1px);

  transform: translateY(1px);
  box-shadow: 0 1px 2px rgba(56, 161, 105, 0.4);
}

.quantity-btn:active:not(:disabled) i {
  transform: scale(0.9);
}

.quantity-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #f1f5f9;
}

.quantity-btn:disabled i {
  color: #a0aec0;
}

/* Add indicator for button functionality */
.quantity-btn:before {
  content: '';
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  border-radius: 50%;
  border: 2px solid transparent;
  transition: all 0.2s;
}

.quantity-btn:focus {
  outline: none;
}

.quantity-btn:focus:before {
  border-color: rgba(22, 163, 74, 0.5);
}

/* Make input more prominent between the buttons */
.quantity-input {
  width: 5rem;
  height: 3rem;
  border-radius: 0.75rem;
  border: 2px solid #e2e8f0;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
  color: #064e3b;
  background-color: white;
  margin: 0 0.5rem;
  transition: all 0.2s ease;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
}

.quantity-input:focus {
  outline: none;
  border-color: #15803d;
  box-shadow: 0 0 0 4px rgba(22, 163, 74, 0.15),
    inset 0 2px 4px rgba(0, 0, 0, 0.03);
}

.special-ticket-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background-color: #ecfdf5;
  border-radius: 0.5rem;
  border: 1px solid #6ee7b7;
}

.special-ticket-info i {
  font-size: 1.25rem;
  color: #16a34a;
}

.special-ticket-info span {
  color: #14532d;
  font-size: 0.95rem;
}
.sort-options {
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.sort-label {
  color: #075985;
  font-weight: 500;
  font-size: 0.95rem;
  margin-right: 0.5rem;
}

.sort-buttons {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.sort-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  border: 1px solid #e2e8f0;
  padding: 0.625rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: #075985;
  cursor: pointer;
  transition: all 0.2s ease;
}

.sort-btn:hover {
  border-color: #cbd5e0;
  background-color: #f7fafc;
  transform: translateY(-1px);
}

.sort-btn.active {
  background: #ecfdf5;
  border-color: #15803d;
  color: #14532d;
  font-weight: 500;
}

/* Responsive design for mobile */
@media (max-width: 640px) {
  .sort-options {
    flex-direction: column;
    align-items: flex-start;
  }

  .sort-buttons {
    width: 100%;
  }

  .sort-btn {
    flex: 1;
    justify-content: center;
  }
}

.input-error {
  border-color: #e53e3e !important;
  box-shadow: 0 0 0 1px #e53e3e !important;
}

.error-message {
  color: #e53e3e;
  font-size: 0.85rem;
  margin-top: 0.5rem;
  font-weight: 500;
}

.validation-warning {
  background-color: #fff5f5;
  color: #e53e3e;
  border-left: 4px solid #e53e3e;
  padding: 0.75rem 1rem;
  margin: 1rem 0;
  border-radius: 0.375rem;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-size: 0.95rem;
  animation: fadeIn 0.3s ease;
}

.validation-warning i {
  font-size: 1.25rem;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.refresh-indicator {
  font-size: 0.75rem;
  color: #718096;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  opacity: 0.7;
  position: absolute;
  right: 1rem;
  top: -1.5rem;
  padding: 0.25rem 0.5rem;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 1rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: opacity 0.2s ease;
}

.refresh-indicator i {
  font-size: 0.7rem;
}

.button-clicked {
  transform: scale(0.98);
  box-shadow: inset 0 0 5px rgba(0, 0, 0, 0.1);
  transition: transform 0.1s, box-shadow 0.1s;
}

.location-btn,
.time-btn {
  position: relative;
  overflow: hidden;
}

.location-btn.active::after,
.time-btn.active::after,
.location-btn:active::after,
.time-btn:active::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to right,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: shimmer 1s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 1rem;
  z-index: 5;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #15803d;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.skeleton-container {
  display: grid;
  /* Use min() to prevent overflow on small screens */
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 250px), 1fr));
  gap: 1rem;
  width: 100%;
  max-width: 100%;
}

.skeleton-card {
  height: 150px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 1rem;
  width: 100%;
  max-width: 100%;
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* No results styles */
.no-results {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  width: 100%;
}

.no-results-content {
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 1rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  max-width: 400px;
}

.no-results-content i {
  font-size: 3rem;
  color: #a0aec0;
  margin-bottom: 1rem;
}

.no-results-content h4 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #064e3b;
  margin-bottom: 0.5rem;
}

.no-results-content p {
  color: #718096;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.clear-search-btn {
  background: #15803d;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.clear-search-btn:hover {
  background: #14532d;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.addons-info {
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;

  h4 {
    font-weight: 600;
    font-size: 1rem;
    color: #064e3b;
    margin-bottom: 0.75rem;
  }
}

.selected-addons-list {
  list-style: none;
  padding: 0;
  margin: 0 0 0.75rem 0;
}

.selected-addon-item {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 10px;
  padding: 0.5rem 0;

  .selected-addon-name {
    font-size: 0.875rem;
    color: #075985;
  }

  .selected-addon-quantity {
    font-size: 0.875rem;
    color: #718096;
  }

  .selected-addon-price {
    font-weight: 500;
    text-align: right;
    color: #064e3b;
  }
}

.addons-total {
  display: flex;
  justify-content: space-between;
  padding-top: 0.75rem;
  border-top: 1px dashed #e2e8f0;
  font-weight: 500;
  color: #075985;
}

.total-price {
  margin-top: 0.5rem;

  .amount {
    color: #15803d;
    font-size: 1.5rem;
    font-weight: 600;
  }
}

/* Addon sticky header - hidden on desktop, visible on mobile */
.addon-sticky-header {
  display: none;
}

@media (max-width: 768px) {
  .addon-step {
    position: relative;
  }

  .addon-sticky-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: sticky;
    top: 0;
    z-index: 100;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    margin-bottom: 1rem;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  .addon-sticky-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .addon-sticky-route {
    color: white;
    font-size: 0.875rem;
    font-weight: 600;
  }

  .addon-sticky-price {
    color: #a7f3d0;
    font-size: 1rem;
    font-weight: 600;
  }

  .addon-sticky-btn {
    background: white;
    color: #667eea;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 0.375rem;
    font-weight: 600;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s;
  }

  .addon-sticky-btn:hover {
    background: #f0f0f0;
  }

  .addon-sticky-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

/* Mobile responsive padding adjustments */
@media (max-width: 640px) {
  .seat-booking {
    padding: 0.5rem !important;
  }

  .step-page,
  .step-card {
    padding: 0.75rem !important; 
  }
}


/* Green/blue brand refresh for public booking wizard */
.seat-booking {
  background-image:
    radial-gradient(circle at 12% 8%, rgba(14, 165, 233, 0.18), transparent 30%),
    radial-gradient(circle at 85% 14%, rgba(22, 163, 74, 0.16), transparent 34%),
    linear-gradient(135deg, rgba(239, 253, 246, 0.96) 0%, rgba(224, 242, 254, 0.9) 100%),
    url('https://images.unsplash.com/photo-1506905925346-21bda4d32df4?q=80&w=2000');
}

.seat-booking__wrapper {
  max-width: 1260px;
  border: 1px solid rgba(14, 165, 233, 0.08);
  border-radius: 2rem;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 8px 30px rgba(6, 78, 59, 0.06), 0 4px 12px rgba(14, 165, 233, 0.06);
}

.step-navigation .step .step-circle {
  border-color: rgba(14, 165, 233, 0.18);
  color: #064e3b;
  box-shadow: 0 8px 24px rgba(14, 165, 233, 0.08);
}

.step-navigation .step .step-label {
  color: #075985;
  font-weight: 600;
}

.step-navigation .step.active .step-circle {
  background: linear-gradient(135deg, #16a34a 0%, #0ea5e9 100%);
  box-shadow: 0 18px 34px rgba(14, 165, 233, 0.24), 0 0 0 8px rgba(22, 163, 74, 0.08);
}

.step-navigation .step.active .step-label,
.step-navigation .step.completed .step-label {
  color: #064e3b;
}

.step-card {
  border: 1px solid rgba(14, 165, 233, 0.08);
  border-radius: 1.5rem;
  background:
    radial-gradient(circle at top right, rgba(14, 165, 233, 0.04), transparent 28%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(248, 253, 250, 0.98));
  box-shadow: 0 4px 12px rgba(6, 78, 59, 0.04);
}

.section-title {
  color: #052e2b;
  font-size: 1.45rem;
  font-weight: 600;
  letter-spacing: -0.02em;
}

.month-filter .month-filter-btn {
  border-color: rgba(14, 165, 233, 0.18);
  color: #075985;
  font-weight: 600;
}

.month-filter .month-filter-btn.active {
  background: linear-gradient(135deg, #16a34a 0%, #0f766e 100%);
  box-shadow: 0 14px 30px rgba(22, 163, 74, 0.22);
}

.search-box input {
  border-color: rgba(14, 165, 233, 0.18);
  color: #064e3b;
  font-weight: 500;
}

.search-box input:focus {
  border-color: #0ea5e9;
  box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.14);
}

.location-btn,
.time-btn {
  border: 1px solid rgba(14, 165, 233, 0.08);
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 2px 8px rgba(6, 78, 59, 0.04);
  font-family: inherit;
}

.location-btn:hover:not(.active),
.time-btn:hover:not(.active) {
  border-color: rgba(14, 165, 233, 0.2);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.08);
}

.location-btn.active,
.time-btn.active {
  border-color: rgba(22, 163, 74, 0.35);
  background: linear-gradient(135deg, #ecfdf5 0%, #e0f2fe 100%);
  box-shadow: 0 4px 12px rgba(22, 163, 74, 0.1);
}

.route-info .cities,
.time-btn .name {
  color: #052e2b;
  font-weight: 500;
}

.route-info .price,
.time-btn .price {
  color: #16a34a;
  font-weight: 600;
}

</style>
